import React, { useEffect, useState, useCallback, useMemo, useRef, memo } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { API, graphqlOperation, Auth } from "aws-amplify";
import {
  listCompanyJobDescriptions,
  listCompanyProfiles,
  listSpotJDS,
  listPrivateScreenings,
  listCandidateProfiles,
  listInterviewSlots,
  getInterviewEvaluation,
  listInterviewEvaluations,
} from "../../graphql/queries";
import JobPostDetails from "./JobPostDetails";
import JobPostForm from "./JobPostForm";
import Header from "../../components/header/Header";
import { Link } from "react-router-dom";
import {
  updateCompanyProfile,
  updateCandidateProfile,
  createPrivateScreening,
  deleteCompanyJobDescription,
  createInterviewSlot,
  updateInterviewSlot,
  updateSpotJD,
  createSpotCandidateStatus,
} from "../../graphql/mutations";
import BuyPremium from "../../components/company/BuyPremium";
import { useNavigate } from "react-router-dom";
import { createPublicInterviewUrl, TOKEN_STATUS } from "../../utils/tokenUtils";
import { findOrCreateCandidateProfile, createMeetingConfig } from "../../utils/candidateUtils";
import { buildResumeURL } from "../../utils/resumeUrl";

const RESUME_PARSER_URL = "https://phone.getscreened.in/parser/upload_resume";
const MAX_RESUME_SIZE_BYTES = 10 * 1024 * 1024;

// API Base URL for knowledge graph services
const KG_API_BASE_URL = 'https://phone.getscreened.in/api';

// Format timestamp to relative time (e.g., "2 days ago") or date.
// Module-scope (pure) so the memoized row below keeps a stable reference.
const formatInvitationTime = (timestamp) => {
  if (!timestamp) return 'N/A';

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  // If older than 7 days, show formatted date
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Probe a recording's duration (seconds) on the client from its URL, using a
// detached <video> element — no backend change needed. Handles the
// fragmented-MP4 case where the browser first reports duration === Infinity by
// seeking far ahead to force the real value to be computed.
const probeVideoDurationSeconds = (url) => new Promise((resolve) => {
  let settled = false;
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.muted = true;
  const cleanup = () => {
    try { video.removeAttribute('src'); video.load(); } catch (e) { /* best effort */ }
  };
  const finish = (sec) => {
    if (settled) return;
    settled = true;
    cleanup();
    resolve(Number.isFinite(sec) && sec > 0 ? sec : null);
  };
  video.onloadedmetadata = () => {
    if (video.duration === Infinity || Number.isNaN(video.duration)) {
      // Streamed/fragmented MP4: force the browser to resolve the real duration.
      video.ontimeupdate = () => { video.ontimeupdate = null; finish(video.duration); };
      try { video.currentTime = 1e7; } catch (e) { finish(null); }
    } else {
      finish(video.duration);
    }
  };
  video.onerror = () => finish(null);
  // Safety timeout so a slow/blocked URL never wedges the probe queue.
  setTimeout(() => finish(video.duration), 12000);
  video.src = url;
});

// "6:14" style label from a seconds value (null when unknown).
const formatDurationShort = (sec) => {
  if (typeof sec !== 'number' || !Number.isFinite(sec) || sec <= 0) return null;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

// Video-duration filter buckets (minutes).
const DURATION_FILTERS = [
  { value: 'all', label: 'All durations' },
  { value: '1-10', label: '1–10 min' },
  { value: '11-20', label: '11–20 min' },
  { value: '21-30', label: '21–30 min' },
  { value: '30+', label: '30+ min' },
];

const durationSecondsInBucket = (sec, bucket) => {
  if (bucket === 'all') return true;
  if (typeof sec !== 'number' || !Number.isFinite(sec) || sec <= 0) return false;
  const min = sec / 60;
  switch (bucket) {
    case '1-10': return min <= 10;
    case '11-20': return min > 10 && min <= 20;
    case '21-30': return min > 20 && min <= 30;
    case '30+': return min > 30;
    default: return true;
  }
};

// Single candidate row, memoized so typing in the search box (or any other
// parent state change) re-renders only the rows whose props actually change
// instead of all 300 at once. The callbacks passed in are stable (see the
// ref-backed wrappers in JobDetailsList), and `isReinviting` is a primitive,
// so React.memo's shallow compare is effective. Markup is identical to the
// previous inline version — this is purely a render-cost change.
const CandidateRow = memo(function CandidateRow({
  candidate,
  idx,
  job,
  jobTitle,
  jobId,
  isReinviting,
  durationSec,
  onWatchVideo,
  onGmail,
  onReinvite,
  onView,
}) {
  return (
    <div
      className="group relative bg-white/5 backdrop-blur-xl rounded hover:bg-white/10 transition-all duration-300"
      style={{
        animation: `fadeInUp 0.3s ease-out forwards`,
        // Cap the stagger: past ~12 rows the cascade added no value but kept
        // the compositor busy for up to 9s with 300 invites.
        animationDelay: `${Math.min(idx, 12) * 0.03}s`,
        opacity: 0
      }}
    >
      {/* Gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-blue-500/5 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

      <div className="relative z-10 px-3 py-1.5">
        <div className="flex items-center gap-2">
          {/* Avatar */}
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
            {candidate.candidateName?.charAt(0).toUpperCase() || 'C'}
          </div>

          {/* Candidate Name - Inline */}
          <div className="flex items-center gap-2 text-xs text-white font-medium min-w-[120px]">
            {candidate.candidateName}
          </div>

          {/* Email - Inline */}
          <div className="flex items-center gap-1 text-[11px] text-gray-400 min-w-[180px] truncate">
            <span className="material-symbols-outlined text-sm">mail</span>
            {candidate.candidateEmail}
          </div>

          {/* Timestamp - Inline */}
          <div className="flex-1 min-w-0 flex items-center gap-1 text-[11px] text-indigo-300 truncate">
            <span className="material-symbols-outlined text-sm">schedule</span>
            <span className="truncate" title={candidate.createdAt ? new Date(candidate.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}>
              Invited {formatInvitationTime(candidate.createdAt)}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {candidate._videoStatus === 'available' && (
              <button
                onClick={() => onWatchVideo(candidate._videoURL)}
                className="px-2 py-1 bg-green-900/40 hover:bg-green-900/60 rounded text-green-300 text-[10px] font-medium transition-all duration-300 flex items-center gap-1 whitespace-nowrap"
                title={formatDurationShort(durationSec) ? `Interview video (${formatDurationShort(durationSec)}) — click to watch` : 'Interview video available — click to watch'}
              >
                <span className="material-symbols-outlined text-sm">videocam</span>
                {formatDurationShort(durationSec) && (
                  <span className="text-[10px] tabular-nums">{formatDurationShort(durationSec)}</span>
                )}
              </button>
            )}
            {candidate._videoStatus === 'missing' && (
              <span
                className="px-2 py-1 bg-red-900/40 rounded text-red-300 text-[10px] font-medium flex items-center gap-1 whitespace-nowrap"
                title="Candidate attended but no video was recorded"
              >
                <span className="material-symbols-outlined text-sm">videocam_off</span>
              </span>
            )}
            <button
              onClick={() => onGmail(candidate.candidateEmail, jobTitle)}
              className="px-2.5 py-1 bg-red-900/30 hover:bg-red-900/50 rounded text-red-400 text-[10px] font-medium transition-all duration-300 flex items-center gap-1 whitespace-nowrap"
              title="Send Gmail"
            >
              <span className="material-symbols-outlined text-sm">mail</span>
              Gmail
            </button>
            <button
              onClick={() => onReinvite(candidate, job)}
              disabled={isReinviting}
              className="px-2.5 py-1 bg-indigo-900/30 hover:bg-indigo-900/50 rounded text-indigo-300 text-[10px] font-medium transition-all duration-300 flex items-center gap-1 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              title="Send a fresh interview invitation with a new slot link (200 credits)"
            >
              <span className={`material-symbols-outlined text-sm ${isReinviting ? 'animate-spin' : ''}`}>
                {isReinviting ? 'progress_activity' : 'forward_to_inbox'}
              </span>
              {isReinviting ? 'Sending...' : 'Reinvite'}
            </button>
            <button
              onClick={() => onView(candidate.candidateEmail, candidate.candidateName, jobId)}
              className="px-2.5 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-[10px] font-medium transition-all duration-300 flex items-center gap-1 whitespace-nowrap"
            >
              <span className="material-symbols-outlined text-sm">visibility</span>
              View
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

const JobDetailsList = () => {
  const [jobDetails, setJobDetails] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [company, setCompany] = useState({});
  const [companyemail, setCompanyemail] = useState("");
  const [companyID, setCompanyID] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [nextToken, setNextToken] = useState(null);
  const [companyList, setCompanyList] = useState([]);
  const [companyjobcount, setcompanyjobcount] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const CompanyEmailID = localStorage.getItem("CompanyEmailID");
  const CompanyProfileID = localStorage.getItem("CompanyProfileID");

  const [loading, setLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);

  const [point, setPoint] = useState(0);
  const [reming, setReming] = useState({
    remainingCredit: point,
  });

  const [showPrivateScreeningModal, setShowPrivateScreeningModal] =
    useState(false);

  // New state for separate modals (merged into one)
  const [showUnifiedModal, setShowUnifiedModal] = useState(false);

  // Sign-in-less interview states
  const [showSignInLessModal, setShowSignInLessModal] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkGenerationError, setLinkGenerationError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Matching Engine states
  const [isRunningMatching, setIsRunningMatching] = useState(false);
  const [matchingJobId, setMatchingJobId] = useState(null);
  const [showMatchingResultModal, setShowMatchingResultModal] = useState(false);
  const [matchingResults, setMatchingResults] = useState([]);
  const [autoFilterEnabled, setAutoFilterEnabled] = useState(false); // Default: false - user chooses to filter
  const [selectedMatchingCandidates, setSelectedMatchingCandidates] = useState([]); // For manual selection
  const [currentSpotJDId, setCurrentSpotJDId] = useState(null); // Store the SpotJD ID for operations

  // New states for inline candidate loading
  const [expandedJobId, setExpandedJobId] = useState(null); // Track which job is expanded
  const [jobCandidates, setJobCandidates] = useState({}); // Store candidates per job
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  // Dashboard mode state - when a job is in "active dashboard" mode
  const [activeJobDashboard, setActiveJobDashboard] = useState(null); // The job currently in full dashboard view
  const [dashboardAnimating, setDashboardAnimating] = useState(false); // Animation state
  const [showAIChat, setShowAIChat] = useState(false); // Floating AI chat visibility

  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  // Candidate sorting state
  const [candidateSortOrder, setCandidateSortOrder] = useState('newest'); // 'newest' or 'oldest'

  // Candidate search: collapsed icon by default; clicking expands an input.
  const [candidateSearchOpen, setCandidateSearchOpen] = useState(false);
  const [candidateSearchQuery, setCandidateSearchQuery] = useState('');
  // Video-duration filter (buckets in DURATION_FILTERS) + probed durations.
  // Durations aren't in the candidate API payload, so we probe each recording's
  // length client-side from its URL (see probeVideoDurationSeconds) and cache it
  // here keyed by video URL → seconds (null when it couldn't be read).
  const [candidateDurationFilter, setCandidateDurationFilter] = useState('all');
  const [videoDurations, setVideoDurations] = useState({});
  const probedVideoUrlsRef = useRef(new Set());

  // In-app video preview modal — opens an HTML5 player with download/PiP
  // disabled so the row's video icon doesn't surface a "Save video as..."
  // option the way a raw tab open does.
  const [videoModalUrl, setVideoModalUrl] = useState(null);

  // Reinvite state: holds the PrivateScreening row id currently being reinvited.
  // Keyed by row id (not email) so duplicate-email rows stay independent.
  const [reinvitingId, setReinvitingId] = useState(null);

  // Reinvite confirmation modal target: { candidate, job } while the modal is open.
  const [reinviteTarget, setReinviteTarget] = useState(null);
  
  // Candidate profile viewing states
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedCandidateProfile, setSelectedCandidateProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [profileError, setProfileError] = useState(null);

  const navigate = useNavigate();
  
  // Debug localStorage values on component mount
  useEffect(() => {
    console.log("=== JobDetailsList Component Debug Info ===");
    console.log("CompanyEmailID:", localStorage.getItem("CompanyEmailID"));
    console.log("CompanyProfileID:", localStorage.getItem("CompanyProfileID"));
    console.log("CompanyProfile:", localStorage.getItem("CompanyProfile"));
    console.log("candidateProfileEmailID:", localStorage.getItem("candidateProfileEmailID"));
    console.log("============================================");
  }, []);
  
  // ===== Helper Functions for Graph Database & SpotJD Updates =====
  
  // Select candidate in graph database
  const selectCandidateInGraph = async (jdId, candidateEmail, roundNumber = 1, notes = '') => {
    try {
      const response = await fetch(`${KG_API_BASE_URL}/kg/spot/select-candidate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jd_id: jdId,
          candidate_email: candidateEmail,
          round_number: roundNumber,
          notes: notes
        })
      });
      
      if (!response.ok) {
        console.warn('Failed to select candidate in graph');
        return null;
      }
      
      const data = await response.json();
      console.log('✅ Candidate selected in graph:', candidateEmail);
      return data;
    } catch (error) {
      console.error('❌ Error selecting candidate in graph:', error);
      return null;
    }
  };

  // Get SpotJD by companyJobDescriptionID
  const getSpotJDByJobDescriptionId = async (jobDescriptionId) => {
    try {
      const response = await API.graphql(
        graphqlOperation(listSpotJDS, {
          filter: { companyJobDescriptionID: { eq: jobDescriptionId } }
        })
      );
      
      const spotJDs = response.data.listSpotJDS.items;
      if (spotJDs.length > 0) {
        return spotJDs[0];
      }
      return null;
    } catch (error) {
      console.error('Error fetching SpotJD:', error);
      return null;
    }
  };

  // Update SpotJD to add candidate to selectedCandidate array
  const addCandidateToSpotJDSelected = async (spotJDId, currentSelected, candidateEmail) => {
    try {
      let selectedArray = [];
      try {
        selectedArray = JSON.parse(currentSelected || '[]');
      } catch (e) {
        selectedArray = [];
      }
      
      if (!selectedArray.includes(candidateEmail)) {
        selectedArray.push(candidateEmail);
      }
      
      const response = await API.graphql({
        query: updateSpotJD,
        variables: {
          input: {
            id: spotJDId,
            selectedCandidate: JSON.stringify(selectedArray)
          }
        }
      });
      
      console.log('✅ SpotJD updated with selected candidate:', candidateEmail);
      return response.data.updateSpotJD;
    } catch (error) {
      console.error('❌ Error updating SpotJD:', error);
      return null;
    }
  };

  // Create SpotCandidateStatus record
  const createCandidateStatusRecord = async (spotJDId, candidateEmail, status = 'selected') => {
    const companyEmail = localStorage.getItem("CompanyEmailID");
    
    try {
      await API.graphql({
        query: createSpotCandidateStatus,
        variables: {
          input: {
            spotJdID: spotJDId,
            companyEmail: companyEmail,
            candidateEmail: candidateEmail,
            roundStatus: status,
            isEmailsend: true,
            selectionFlag: status,
            candidateFrom: 'private_screening'
          }
        }
      });
      console.log(`✅ Created candidate status for: ${candidateEmail}`);
      return true;
    } catch (error) {
      console.error(`❌ Error creating status for ${candidateEmail}:`, error);
      return false;
    }
  };

  // Filter candidate in graph database
  const filterCandidateInGraph = async (jdId, candidateEmail, roundNumber = 1, notes = '') => {
    try {
      const response = await fetch(`${KG_API_BASE_URL}/kg/spot/filter-candidate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jd_id: jdId,
          candidate_email: candidateEmail,
          round_number: roundNumber,
          notes: notes
        })
      });
      
      if (!response.ok) {
        console.warn('Failed to filter candidate in graph');
        return null;
      }
      
      const data = await response.json();
      console.log('✅ Candidate filtered in graph:', candidateEmail);
      return data;
    } catch (error) {
      console.error('❌ Error filtering candidate in graph:', error);
      return null;
    }
  };

  // Run Matching Engine for a specific job
  const handleRunMatchingEngine = async (job) => {
    setMatchingJobId(job.id);
    setIsRunningMatching(true);
    setSelectedMatchingCandidates([]); // Reset selection
    
    try {
      // First get the SpotJD for this job
      const spotJD = await getSpotJDByJobDescriptionId(job.id);

      if (!spotJD) {
        alert('No SpotJD found for this job. Please ensure the job was created properly.');
        return;
      }

      // Store the SpotJD ID for later operations
      setCurrentSpotJDId(spotJD.id);

      // Run matching engine using SpotJD ID (not job description ID)
      const response = await fetch(`${KG_API_BASE_URL}/matching/match-candidates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: spotJD.id })
      });

      if (!response.ok) {
        throw new Error('Failed to run matching engine');
      }

      const data = await response.json();
      const candidates = data.candidates || data.matched_candidates || data.results || [];
      
      console.log('✅ Matching engine results:', candidates.length, 'candidates found');

      if (candidates.length > 0) {
        setMatchingResults(candidates);
        setShowMatchingResultModal(true);
        
        // Only auto-filter if enabled (default is false)
        if (autoFilterEnabled) {
          await autoFilterCandidates(spotJD.id, candidates);
        }
      } else {
        alert('No matching candidates found for this job description.');
      }
    } catch (error) {
      console.error('❌ Error running matching engine:', error);
      alert('Error running matching engine: ' + error.message);
    } finally {
      setIsRunningMatching(false);
      setMatchingJobId(null);
    }
  };

  // Auto-filter all matched candidates
  const autoFilterCandidates = async (spotJDId, candidates) => {
    try {
      // Get current SpotJD
      const spotJDResponse = await API.graphql(
        graphqlOperation(listSpotJDS, {
          filter: { id: { eq: spotJDId } }
        })
      );
      
      const spotJD = spotJDResponse.data.listSpotJDS.items[0];
      if (!spotJD) return;

      // Get existing filtered candidates
      let existingFiltered = [];
      try {
        existingFiltered = JSON.parse(spotJD.filterCandidate || '[]');
      } catch (e) {
        existingFiltered = [];
      }

      // Add new candidates to filtered list
      const newEmails = candidates.map(c => c.email || c.candidate_email);
      const allFiltered = [...new Set([...existingFiltered, ...newEmails])];

      // Update SpotJD
      await API.graphql({
        query: updateSpotJD,
        variables: {
          input: {
            id: spotJDId,
            filterCandidate: JSON.stringify(allFiltered)
          }
        }
      });

      // Create SpotCandidateStatus for each and call filter API
      const companyEmail = localStorage.getItem("CompanyEmailID");
      for (const candidate of candidates) {
        const email = candidate.email || candidate.candidate_email;
        
        // Create status record
        try {
          await API.graphql({
            query: createSpotCandidateStatus,
            variables: {
              input: {
                spotJdID: spotJDId,
                companyEmail: companyEmail,
                candidateEmail: email,
                roundStatus: 'filtered',
                isEmailsend: false,
                selectionFlag: 'filtered',
                candidateFrom: 'matching_engine'
              }
            }
          });
        } catch (e) {
          console.warn('Status record may already exist:', email);
        }

        // Call filter-candidate API
        await filterCandidateInGraph(spotJDId, email, 1, 'Filtered from matching engine');
      }

      console.log('✅ Auto-filtered', candidates.length, 'candidates');
    } catch (error) {
      console.error('❌ Error auto-filtering candidates:', error);
    }
  };

  // Toggle individual candidate selection
  const toggleCandidateSelection = (candidate) => {
    const email = candidate.email || candidate.candidate_email;
    setSelectedMatchingCandidates(prev => {
      const isSelected = prev.some(c => (c.email || c.candidate_email) === email);
      if (isSelected) {
        return prev.filter(c => (c.email || c.candidate_email) !== email);
      } else {
        return [...prev, candidate];
      }
    });
  };

  // Select all matching candidates
  const selectAllMatchingCandidates = () => {
    setSelectedMatchingCandidates([...matchingResults]);
  };

  // Deselect all matching candidates
  const deselectAllMatchingCandidates = () => {
    setSelectedMatchingCandidates([]);
  };

  // Filter selected candidates manually (uses SpotJD ID)
  const filterSelectedCandidates = async () => {
    if (!currentSpotJDId || selectedMatchingCandidates.length === 0) {
      alert('Please select at least one candidate to filter.');
      return;
    }

    setIsRunningMatching(true);
    try {
      await autoFilterCandidates(currentSpotJDId, selectedMatchingCandidates);
      alert(`Successfully filtered ${selectedMatchingCandidates.length} candidates!`);
      
      // Mark these candidates as filtered in the UI
      setMatchingResults(prev => prev.map(c => {
        const email = c.email || c.candidate_email;
        const isFiltered = selectedMatchingCandidates.some(sc => (sc.email || sc.candidate_email) === email);
        return isFiltered ? { ...c, isFiltered: true } : c;
      }));
      
      setSelectedMatchingCandidates([]);
    } catch (error) {
      console.error('❌ Error filtering selected candidates:', error);
      alert('Error filtering candidates: ' + error.message);
    } finally {
      setIsRunningMatching(false);
    }
  };

  // Select candidates for private screening (uses SpotJD ID)
  const selectCandidatesForScreening = async () => {
    if (!currentSpotJDId || selectedMatchingCandidates.length === 0) {
      alert('Please select at least one candidate for screening.');
      return;
    }

    setIsRunningMatching(true);
    try {
      for (const candidate of selectedMatchingCandidates) {
        const email = candidate.email || candidate.candidate_email;
        
        // Add to selected candidates in SpotJD
        const spotJDResponse = await API.graphql(
          graphqlOperation(listSpotJDS, {
            filter: { id: { eq: currentSpotJDId } }
          })
        );
        const spotJD = spotJDResponse.data.listSpotJDS.items[0];
        if (spotJD) {
          let existingSelected = [];
          try {
            existingSelected = JSON.parse(spotJD.selectedCandidate || '[]');
          } catch (e) {
            existingSelected = [];
          }
          
          if (!existingSelected.includes(email)) {
            existingSelected.push(email);
            await API.graphql({
              query: updateSpotJD,
              variables: {
                input: {
                  id: currentSpotJDId,
                  selectedCandidate: JSON.stringify(existingSelected)
                }
              }
            });
          }
        }

        // Create status record
        const companyEmail = localStorage.getItem("CompanyEmailID");
        try {
          await API.graphql({
            query: createSpotCandidateStatus,
            variables: {
              input: {
                spotJdID: currentSpotJDId,
                companyEmail: companyEmail,
                candidateEmail: email,
                roundStatus: 'selected',
                isEmailsend: false,
                selectionFlag: 'selected',
                candidateFrom: 'matching_engine'
              }
            }
          });
        } catch (e) {
          console.warn('Status record may already exist:', email);
        }

        // Call select-candidate API with SpotJD ID
        await selectCandidateInGraph(currentSpotJDId, email, 1, 'Selected from matching engine');
      }

      alert(`Successfully selected ${selectedMatchingCandidates.length} candidates for screening!`);
      
      // Mark these candidates as selected in the UI
      setMatchingResults(prev => prev.map(c => {
        const email = c.email || c.candidate_email;
        const isSelected = selectedMatchingCandidates.some(sc => (sc.email || sc.candidate_email) === email);
        return isSelected ? { ...c, isSelected: true } : c;
      }));
      
      setSelectedMatchingCandidates([]);
    } catch (error) {
      console.error('❌ Error selecting candidates:', error);
      alert('Error selecting candidates: ' + error.message);
    } finally {
      setIsRunningMatching(false);
    }
  };

  // Fetch private screening candidates for a specific job
  const fetchCandidatesForJob = useCallback(async (jobId) => {
    setLoadingCandidates(true);
    try {
      const storedCompanyID = localStorage.getItem("CompanyProfileID");

      let allCandidates = [];
      let nextToken = null;

      do {
        const response = await API.graphql(
          graphqlOperation(listPrivateScreenings, {
            filter: {
              companyProfileID: { eq: storedCompanyID },
              jdID: { eq: jobId }
            },
            limit: 100,
            nextToken: nextToken,
          })
        );

        const { items, nextToken: newToken } = response.data.listPrivateScreenings;
        allCandidates.push(...items);
        nextToken = newToken;
      } while (nextToken);

      // Enrich each candidate with interview + video status so the row can
      // render a "video available" / "attended but no video" badge without a
      // per-row network call. Wrapped in its own try so a failure here
      // doesn't blank the candidate list.
      let enrichedCandidates = allCandidates;
      try {
        let allSlots = [];
        let slotToken = null;
        do {
          const slotsResp = await API.graphql(
            graphqlOperation(listInterviewSlots, {
              filter: { jdID: { eq: jobId } },
              limit: 100,
              nextToken: slotToken,
            })
          );
          const { items: slotItems, nextToken: nextSlotToken } = slotsResp.data.listInterviewSlots;
          allSlots.push(...slotItems);
          slotToken = nextSlotToken;
        } while (slotToken);

        const normalizeEmail = (e) => (e || '').trim().toLowerCase();

        // Group slots by candidateEmail, keep most recent first
        const slotsByEmail = new Map();
        for (const slot of allSlots) {
          const key = normalizeEmail(slot.candidateEmail);
          if (!key) continue;
          if (!slotsByEmail.has(key)) slotsByEmail.set(key, []);
          slotsByEmail.get(key).push(slot);
        }
        for (const slots of slotsByEmail.values()) {
          slots.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        }

        // Pair each PrivateScreening row with its own InterviewSlot so a
        // reinvite doesn't make the original invite "inherit" the new
        // interview's video. Both lists are sorted newest-first per email
        // and zipped by index: i-th invite ↔ i-th slot. The reinvite flow
        // creates slot then screening back-to-back, so per-email createdAt
        // ordering is stable across the two tables.
        const screeningsByEmail = new Map();
        for (const c of allCandidates) {
          const key = normalizeEmail(c.candidateEmail);
          if (!key) continue;
          if (!screeningsByEmail.has(key)) screeningsByEmail.set(key, []);
          screeningsByEmail.get(key).push(c);
        }
        for (const list of screeningsByEmail.values()) {
          list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        }
        const slotByScreeningId = new Map();
        for (const [email, screenings] of screeningsByEmail.entries()) {
          const slots = slotsByEmail.get(email) || [];
          screenings.forEach((ps, idx) => {
            slotByScreeningId.set(ps.id, slots[idx] || null);
          });
        }

        // Batch-fetch evaluations for every paired slot so each invite row
        // can resolve its own evaluation (was: most-recent slot per email,
        // which collapsed reinvite + original into the same eval).
        const evalIdsToFetch = [];
        const slotIdsNeedingFallback = [];
        for (const slot of slotByScreeningId.values()) {
          if (!slot) continue;
          if (slot.interviewSlotInterviewEvaluationId) {
            evalIdsToFetch.push(slot.interviewSlotInterviewEvaluationId);
          } else if (slot.id) {
            // Slot exists but its back-link to the evaluation isn't set —
            // common when interview-completion creates the eval but never
            // writes interviewSlotInterviewEvaluationId on the slot. We'll
            // find the eval by querying from the other side (eval.interviewSlotID).
            slotIdsNeedingFallback.push(slot.id);
          }
        }

        // Helper: split an array into fixed-size chunks. Large invite lists
        // (~300) break both a giant AppSync `or` filter and a single huge
        // parallel fan-out, so every backend call below is chunked to stay
        // within limits — at ~50 invites these all fit in one chunk anyway.
        const chunk = (arr, size) => {
          const out = [];
          for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
          return out;
        };

        const evalMap = new Map();
        if (evalIdsToFetch.length > 0) {
          // Batch the by-id fetches so 300 invites don't fire 300 parallel
          // requests at once (which AppSync throttles, dropping video icons).
          for (const idsBatch of chunk(evalIdsToFetch, 50)) {
            const results = await Promise.allSettled(
              idsBatch.map((evalId) =>
                API.graphql(graphqlOperation(getInterviewEvaluation, { id: evalId }))
              )
            );
            results.forEach((res, idx) => {
              if (res.status === 'fulfilled' && res.value?.data?.getInterviewEvaluation) {
                const ev = res.value.data.getInterviewEvaluation;
                evalMap.set(idsBatch[idx], ev);
              }
            });
          }
        }

        // Fallback: for slots without the FK set, find their eval via
        // listInterviewEvaluations filtered by interviewSlotID. Single
        // bulk query with OR over the missing slot IDs, paginated.
        const evalBySlotIdMap = new Map();
        if (slotIdsNeedingFallback.length > 0) {
          try {
            let fallbackEvals = [];
            // An `or` filter over EVERY missing slot id exceeds AppSync's
            // filter-expression limit once invites grow large (~300), which
            // threw and wiped all video badges. Query in small OR-batches.
            for (const slotIdBatch of chunk(slotIdsNeedingFallback, 25)) {
              const orFilter = {
                or: slotIdBatch.map((id) => ({ interviewSlotID: { eq: id } }))
              };
              let evalToken = null;
              do {
                const evalResp = await API.graphql(
                  graphqlOperation(listInterviewEvaluations, {
                    filter: orFilter,
                    limit: 100,
                    nextToken: evalToken,
                  })
                );
                const { items: evalItems, nextToken: nextEvalToken } = evalResp.data.listInterviewEvaluations;
                fallbackEvals.push(...evalItems);
                evalToken = nextEvalToken;
              } while (evalToken);
            }

            // If multiple evals exist for the same slot (e.g. reappear rounds),
            // keep the most recent one — it's the one that should carry the
            // current video URL.
            for (const ev of fallbackEvals) {
              if (!ev.interviewSlotID) continue;
              const existing = evalBySlotIdMap.get(ev.interviewSlotID);
              if (!existing || new Date(ev.createdAt || 0) > new Date(existing.createdAt || 0)) {
                evalBySlotIdMap.set(ev.interviewSlotID, ev);
              }
            }
          } catch (fallbackErr) {
            console.warn('⚠️ Eval slot-id fallback failed:', fallbackErr);
          }
        }

        // Extract video URL from an evaluation, mirroring the View page logic:
        // the dedicated `videoURL` column is often empty, but the URL is
        // commonly persisted inside the stringified `evaluation` JSON blob
        // under one of several legacy field names. Check both sources so the
        // row icon matches what View actually shows.
        const extractVideoUrl = (evalRecord) => {
          if (!evalRecord) return null;
          const direct = evalRecord.videoURL && String(evalRecord.videoURL).trim();
          if (direct) return String(evalRecord.videoURL).trim();
          const blob = evalRecord.evaluation;
          if (!blob) return null;
          try {
            const parsed = typeof blob === 'string' ? JSON.parse(blob) : blob;
            if (parsed && typeof parsed === 'object') {
              const videoFields = ['videoURL', 'video_url', 'videoUrl', 'recordingUrl', 'recording_url', 'interviewVideo', 'interview_video'];
              for (const f of videoFields) {
                if (parsed[f] && typeof parsed[f] === 'string' && parsed[f].trim()) {
                  return parsed[f].trim();
                }
              }
            }
          } catch (e) { /* not JSON, skip */ }
          return null;
        };

        enrichedCandidates = allCandidates.map((c) => {
          const pairedSlot = slotByScreeningId.get(c.id) || null;
          const attended = !!(pairedSlot && (pairedSlot.candidateAttendance === true || pairedSlot.botAttendance === true));
          const evaluation = (pairedSlot?.interviewSlotInterviewEvaluationId
            ? evalMap.get(pairedSlot.interviewSlotInterviewEvaluationId)
            : null) || (pairedSlot?.id ? evalBySlotIdMap.get(pairedSlot.id) : null);
          const videoURL = extractVideoUrl(evaluation);
          let _videoStatus = null;
          if (videoURL) _videoStatus = 'available';
          else if (attended) _videoStatus = 'missing';
          return { ...c, _videoStatus, _videoURL: videoURL, _attended: attended, _slotId: pairedSlot?.id || null };
        });

        // Diagnostic: one line per invite row, showing which specific slot
        // it was paired with. pairedSlotId===null means the candidate has
        // more PrivateScreening rows than InterviewSlots for that email
        // (e.g. a legacy invite created before slot-creation was wired in).
        console.log(`🎥 Video status for ${enrichedCandidates.length} candidates (job ${jobId}); total slots fetched for this jdID: ${allSlots.length}`);
        console.table(enrichedCandidates.map((c) => {
          const pairedSlot = slotByScreeningId.get(c.id) || null;
          const fkEvalId = pairedSlot?.interviewSlotInterviewEvaluationId ?? null;
          const fallbackEval = pairedSlot?.id ? evalBySlotIdMap.get(pairedSlot.id) : null;
          return {
            name: c.candidateName,
            email: c.candidateEmail,
            pairedSlotId: pairedSlot?.id ?? null,
            candidateAttendance: pairedSlot?.candidateAttendance ?? null,
            botAttendance: pairedSlot?.botAttendance ?? null,
            evalFromFK: fkEvalId,
            evalFromSlotId: fallbackEval?.id ?? null,
            videoURL: c._videoURL,
            _videoStatus: c._videoStatus,
          };
        }));
      } catch (enrichErr) {
        console.warn('⚠️ Video status enrichment failed — list will render without badges:', enrichErr);
      }

      setJobCandidates(prev => ({
        ...prev,
        [jobId]: enrichedCandidates
      }));

      console.log(`✅ Loaded ${enrichedCandidates.length} candidates for job ${jobId}`);
    } catch (error) {
      console.error('Error fetching candidates for job:', error);
      setJobCandidates(prev => ({
        ...prev,
        [jobId]: []
      }));
    } finally {
      setLoadingCandidates(false);
    }
  }, []);

  // Handle job expansion toggle
  const handleJobExpand = async (jobId) => {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
      setSelectedJob(null);
    } else {
      const job = jobDetails.find(j => j.id === jobId);
      setExpandedJobId(jobId);
      setSelectedJob(job);
      
      // Fetch candidates if not already loaded
      if (!jobCandidates[jobId]) {
        await fetchCandidatesForJob(jobId);
      }
    }
  };

  // Handle activating dashboard mode for a job
  const activateDashboardMode = async (job) => {
    setDashboardAnimating(true);
    setSelectedJob(job);
    
    // Small delay for animation
    setTimeout(() => {
      setActiveJobDashboard(job);
      setDashboardAnimating(false);
      
      // Fetch candidates if not already loaded
      if (!jobCandidates[job.id]) {
        fetchCandidatesForJob(job.id);
      }
    }, 300);
  };

  // Exit dashboard mode
  const exitDashboardMode = () => {
    setDashboardAnimating(true);
    setTimeout(() => {
      setActiveJobDashboard(null);
      setSelectedJob(null);
      setDashboardAnimating(false);
    }, 300);
  };

  // Navigate to SpotGPT with job context
  const openSpotGPT = () => {
    if (activeJobDashboard) {
      navigate('/spotgpt', { state: { jobId: activeJobDashboard.id, jobTitle: activeJobDashboard.jobTitle } });
    } else {
      navigate('/spotgpt');
    }
  };

  // Open Gmail compose window for quick email
  const openGmailCompose = (candidateEmail, jobTitle) => {
    console.log('Opening Gmail for:', candidateEmail);
    const subject = encodeURIComponent(`Interview Invitation - ${jobTitle}`);
    const body = encodeURIComponent(`Dear Candidate,\n\nWe are pleased to invite you for an interview for the position of ${jobTitle}.\n\nPlease let us know your availability.\n\nBest regards`);
    window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${candidateEmail}&su=${subject}&body=${body}`, '_blank');
    
    // Show success toast
    console.log('Setting toast state:', candidateEmail);
    setToast({ show: true, message: `📧 Opening Gmail for ${candidateEmail}`, type: 'success' });
    setTimeout(() => {
      console.log('Hiding toast');
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  };
  
  // Sort candidates by invitation date
  const sortCandidatesByDate = (candidates) => {
    if (!candidates || candidates.length === 0) return candidates;
    
    return [...candidates].sort((a, b) => {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      
      if (candidateSortOrder === 'newest') {
        return dateB - dateA; // Newest first
      } else {
        return dateA - dateB; // Oldest first
      }
    });
  };

  // Probe recording durations for the active dashboard's candidates that have a
  // video, once per URL. Runs in the background; results land in videoDurations
  // and feed both the row badge and the duration filter. The ref guards against
  // re-probing the same URL across re-renders / refreshes.
  useEffect(() => {
    const list = (activeJobDashboard && jobCandidates[activeJobDashboard.id]) || [];
    const toProbe = list.filter(
      c => c._videoStatus === 'available' && c._videoURL && !probedVideoUrlsRef.current.has(c._videoURL)
    );
    if (toProbe.length === 0) return;

    let cancelled = false;
    (async () => {
      // Sequential probing keeps us from opening dozens of media connections at
      // once; each URL is cached so this only pays the cost once.
      for (const c of toProbe) {
        if (cancelled) break;
        probedVideoUrlsRef.current.add(c._videoURL);
        const sec = await probeVideoDurationSeconds(c._videoURL);
        if (cancelled) break;
        setVideoDurations(prev => ({ ...prev, [c._videoURL]: sec }));
      }
    })();
    return () => { cancelled = true; };
  }, [activeJobDashboard, jobCandidates]);

  // Memoized sorted + filtered candidate list for the active dashboard. Without
  // this, every keystroke in the search box (or any unrelated re-render) re-sorts
  // and re-filters all 300 rows. Recomputes only when its real inputs change.
  const dashboardCandidatesView = useMemo(() => {
    const list = activeJobDashboard ? jobCandidates[activeJobDashboard.id] : null;
    if (!list || list.length === 0) return [];
    const sorted = sortCandidatesByDate(list);
    const q = candidateSearchQuery.trim().toLowerCase();
    let result = !q ? sorted : sorted.filter(c =>
      (c.candidateName || '').toLowerCase().includes(q) ||
      (c.candidateEmail || '').toLowerCase().includes(q)
    );
    // Duration bucket filter. Candidates whose duration isn't known yet (no
    // video, probe still running, or unreadable) are excluded while a specific
    // bucket is active; they reappear once probed (or when set back to "All").
    if (candidateDurationFilter !== 'all') {
      result = result.filter(c =>
        durationSecondsInBucket(c._videoURL ? videoDurations[c._videoURL] : null, candidateDurationFilter)
      );
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJobDashboard, jobCandidates, candidateSearchQuery, candidateSortOrder, candidateDurationFilter, videoDurations]);

  // Stable, ref-backed wrappers for the row action handlers. The underlying
  // handlers are redefined every render, which would defeat React.memo on the
  // rows; these wrappers keep a constant identity while always invoking the
  // latest handler via the ref (set just before render returns).
  const rowHandlersRef = useRef({});
  const onWatchVideo = useCallback((url) => rowHandlersRef.current.setVideoModalUrl(url), []);
  const onGmail = useCallback((email, jobTitle) => rowHandlersRef.current.openGmailCompose(email, jobTitle), []);
  const onReinviteRow = useCallback((candidate, job) => rowHandlersRef.current.handleReinviteCandidate(candidate, job), []);
  const onViewRow = useCallback((email, name, jobId) => rowHandlersRef.current.viewCandidateProfile(email, name, jobId), []);

  // Fetch and display candidate profile
  const viewCandidateProfile = async (candidateEmail, candidateName, jobId = null) => {
    setLoadingProfile(true);
    setProfileError(null);
    setShowProfileModal(true);
    
    try {
      // Fetch candidate profile
      let nextToken = null;
      let userProfileList = [];
      
      do {
        const userProfileResponse = await API.graphql(
          graphqlOperation(listCandidateProfiles, {
            filter: { email: { eq: candidateEmail } },
            limit: 100,
            nextToken: nextToken,
          })
        );
        
        userProfileList.push(
          ...userProfileResponse.data.listCandidateProfiles.items
        );
        nextToken = userProfileResponse.data.listCandidateProfiles.nextToken;
      } while (nextToken);
      
      if (userProfileList.length > 0) {
        const profile = userProfileList[0];
        
        // Fetch interview slots for this candidate
        let slotNextToken = null;
        let interviewSlots = [];
        
        do {
          const slotFilter = {
            candidateEmail: { eq: candidateEmail },
            ...(jobId ? { jdID: { eq: jobId } } : {})
          };

          const slotResponse = await API.graphql(
            graphqlOperation(listInterviewSlots, {
              filter: slotFilter,
              limit: 100,
              nextToken: slotNextToken,
            })
          );
          
          interviewSlots.push(
            ...slotResponse.data.listInterviewSlots.items
          );
          slotNextToken = slotResponse.data.listInterviewSlots.nextToken;
        } while (slotNextToken);
        
        // Get the most recent completed interview slot
        const completedSlots = interviewSlots.filter(
          slot => slot.candidateAttendance === true || slot.botAttendance === true
        );
        
        // Sort by date (most recent first)
        const sortedSlots = completedSlots.sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA;
        });
        
        // Use the most recent slot, or any slot if no completed slots
        const slotToUse = sortedSlots.length > 0 ? sortedSlots[0] : 
                         (interviewSlots.length > 0 ? interviewSlots[0] : null);
        
        setSelectedCandidateProfile({
          ...profile,
          interviewSlot: slotToUse,
          allInterviewSlots: interviewSlots,
          interviewContextJobId: jobId,
          hasCompletedInterview: completedSlots.length > 0
        });
      } else {
        setProfileError('No profile found for this candidate. They may not have completed registration.');
        setSelectedCandidateProfile({
          email: candidateEmail,
          fullName: candidateName,
          noProfile: true
        });
      }
    } catch (error) {
      console.error('Error fetching candidate profile:', error);
      setProfileError('Failed to load candidate profile. Please try again.');
    } finally {
      setLoadingProfile(false);
    }
  };
  
  // Close profile modal
  const closeProfileModal = () => {
    setShowProfileModal(false);
    setSelectedCandidateProfile(null);
    setProfileError(null);
  };
  
  useEffect(() => {
    const companyProfileID = localStorage.getItem("CompanyProfileID");
    console.log("JobDetailsList useEffect - CompanyProfileID from localStorage:", companyProfileID);
    
    if (companyProfileID) {
      fetchJobDetails(companyProfileID);
    } else {
      console.warn("No CompanyProfileID found in localStorage - attempting to fetch company data");
      
      // Try to get authenticated user email first (same as CompanyHome.js logic)
      const fetchAuthenticatedUserAndCompany = async () => {
        try {
          // First priority: Get authenticated user email
          let emailToUse = null;
          try {
            const currentUser = await Auth.currentAuthenticatedUser();
            emailToUse = currentUser.attributes.email;
            console.log("JobDetailsList - Found authenticated user:", emailToUse);
            
            // Update localStorage with fresh email
            localStorage.setItem("candidateProfileEmailID", emailToUse);
          } catch (authError) {
            console.warn("JobDetailsList - No authenticated user, checking localStorage:", authError);
            // Fallback to localStorage
            emailToUse = localStorage.getItem("candidateProfileEmailID");
          }
          
          if (emailToUse) {
            console.log("JobDetailsList - Using email for company lookup:", emailToUse);
            await fetchCompanyDataAndThenJobs(emailToUse);
          } else {
            console.error("JobDetailsList - No email found for company lookup - user may need to re-authenticate");
            setIsLoading(false);
          }
        } catch (error) {
          console.error("JobDetailsList - Error in authentication flow:", error);
          setIsLoading(false);
        }
      };

      fetchAuthenticatedUserAndCompany();
    }
  }, []);

  const closeModal = () => {
    console.log("Closing modal");
    setIsModalOpen(false);
  };
  const handleProfileClickOpen = () => {
    setIsProfileModalOpen(true);
  };
  const handleProfileClickClose = () => {
    setIsProfileModalOpen(false);
  };
  useEffect(() => {
    const storedCompanyEmail = localStorage.getItem("CompanyEmailID");
    const storedCompanyID = localStorage.getItem("CompanyProfileID");
    const storedCompanyProfile = localStorage.getItem("CompanyProfile");

    // Load company data from localStorage (same as CompanyHome.js)
    if (storedCompanyProfile) {
      try {
        const parsedCompanyData = JSON.parse(storedCompanyProfile);
        setCompany(parsedCompanyData);
        setCompanyName(parsedCompanyData.companyName || "");
        setCompanyemail(parsedCompanyData.email || storedCompanyEmail || "");
        setCompanyID(parsedCompanyData.id || storedCompanyID || "");
      } catch (e) {
        console.warn("Error parsing company profile from localStorage:", e);
        setCompanyemail(storedCompanyEmail || "");
        setCompanyID(storedCompanyID || "");
      }
    } else {
      setCompanyemail(storedCompanyEmail || "");
      setCompanyID(storedCompanyID || "");
    }

    const fetchData = async () => {
      try {
        const storedCompanyEmail = localStorage.getItem("CompanyEmailID");
        const storedCompanyID = localStorage.getItem("CompanyProfileID");

        // If we already have company data in localStorage, use it
        const storedCompanyProfile = localStorage.getItem("CompanyProfile");
        if (storedCompanyProfile) {
          try {
            const parsedCompanyData = JSON.parse(storedCompanyProfile);
            setCompany(parsedCompanyData);
            setCompanyName(parsedCompanyData.companyName || "");
            setCompanyemail(parsedCompanyData.email || storedCompanyEmail || "");
            setPoint(parsedCompanyData.remainingCredit || 0);
            setCompanyID(parsedCompanyData.id || storedCompanyID || "");
            setLoading(false);
            // Don't return here: the cached value gives an instant paint, but we
            // still fall through to the API fetch below so a page refresh picks up
            // newly granted credits (remainingCredit) instead of staying stale.
          } catch (parseError) {
            console.warn("Error parsing stored company profile:", parseError);
          }
        }

        if (!storedCompanyEmail) {
          console.warn("No company email found in localStorage");
          setLoading(false);
          return;
        }

        const { data } = await API.graphql(
          graphqlOperation(listCompanyProfiles, {
            filter: {
              email: { eq: storedCompanyEmail },
            },
            limit: 100, // Limit the number of items per page, adjust as needed
            nextToken: nextToken, // Pass nextToken if available
          })
        );

        if (
          data &&
          data.listCompanyProfiles.items &&
          data.listCompanyProfiles.items.length > 0
        ) {
          const companyData = data.listCompanyProfiles.items[0];
          setCompany(companyData);
          setCompanyName(companyData.companyName || "");
          setCompanyemail(companyData.email || storedCompanyEmail || "");
          setPoint(companyData.remainingCredit || 0);
          setCompanyID(companyData.id || "");

          // Update localStorage with fresh data
          localStorage.setItem("CompanyProfile", JSON.stringify(companyData));
          localStorage.setItem("CompanyProfileID", companyData.id);
          localStorage.setItem("CompanyEmailID", companyData.email);

          const { items, nextToken: newNextToken } = data.listCompanyProfiles;
          setCompanyList((prevList) => [...prevList, ...items]);
          setNextToken(newNextToken);
          setLoading(false);
        } else {
          console.warn("No company profile found for email:", storedCompanyEmail);
          setLoading(false); // Set loading to false if no data is found
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        // Handle error
        setLoading(false); // Set loading to false on error
      }
    };

    fetchData();

    // fetching job description count code start

    // const fetchJobCount = async() => {

    //   const storedCompanyID = localStorage.getItem("CompanyProfileID");

    //   // Fetch user profile with pagination
    //   let nextToken = null;
    //   let userProfileList = [];
    //   do {
    //     const joblistResponse  = await API.graphql(
    //       graphqlOperation(listCompanyJobDescriptions, {
    //         filter: { companyProfileID: { eq: storedCompanyID } },
    //         limit: 100,
    //         nextToken: nextToken,
    //       })
    //     );
    //     console.log("####################### from job details list###############3",joblistResponse)
    //     userProfileList.push(
    //       ...joblistResponse.data.listCompanyProfiles.items
    //     );
    //     nextToken = joblistResponse.data.listCompanyProfiles.nextToken;
    //   } while (nextToken);

    //   // Set compnay count
    //   // if (userProfileList.length > 0) {
    //   //   // setUserFullName(userProfileList[0].fullName);
    //   //   setcompanyjobcount(true)

    //   // } else {
    //   //   console.log("User profile not found.");
    //   //   setcompanyjobcount(false)
    //   // }

    // }

    // fetchJobCount()

    const fetchJobCount = async () => {
      const storedCompanyID = localStorage.getItem("CompanyProfileID");

      try {
        let nextToken = null;
        let jobCount = 0;

        do {
          const jobListResponse = await API.graphql(
            graphqlOperation(listCompanyJobDescriptions, {
              filter: { companyProfileID: { eq: storedCompanyID } },
              limit: 100,
              nextToken: nextToken,
            })
          );

          const jobDescriptions =
            jobListResponse.data.listCompanyJobDescriptions.items;
          jobCount += jobDescriptions.length;

          nextToken = jobListResponse.data.listCompanyJobDescriptions.nextToken;
        } while (nextToken);

        // Set company count
        if (jobCount > 0) {
          // You can set company job count here
          setcompanyjobcount(true);
        } else {
          console.log("No job descriptions found for the company.");
          setcompanyjobcount(false);
        }
      } catch (error) {
        console.error("Error fetching job count:", error);
        // Handle error appropriately
      }
    };

    fetchJobCount();

    // fetching job description count code end
  }, []); // Remove nextToken dependency to prevent infinite re-renders

  // console.log("compay count", companyjobcount); // Remove or comment out repetitive console log

  // Fetch company data first, then job details - for cases where localStorage is empty
  const fetchCompanyDataAndThenJobs = async (userEmail) => {
    try {
      console.log("JobDetailsList - Fetching company data for email:", userEmail);
      
      // First, try to get the authenticated user's email (same logic as CompanyHome.js)
      let authenticatedEmail = null;
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        authenticatedEmail = currentUser.attributes.email;
        console.log("JobDetailsList - Authenticated user email:", authenticatedEmail);
        
        // Update localStorage with fresh authenticated email
        localStorage.setItem("candidateProfileEmailID", authenticatedEmail);
      } catch (authError) {
        console.warn("JobDetailsList - No authenticated user, using provided email:", authError);
      }

      // Use authenticated email if available, otherwise fall back to provided email
      const emailToUse = authenticatedEmail || userEmail;
      
      if (!emailToUse) {
        console.warn("JobDetailsList - No email available for company lookup");
        setIsLoading(false);
        return;
      }
      
      const { data } = await API.graphql(
        graphqlOperation(listCompanyProfiles, {
          filter: {
            email: { eq: emailToUse },
          },
          limit: 100,
        })
      );
      
      console.log("JobDetailsList - GraphQL response:", data);
      
      if (
        data &&
        data.listCompanyProfiles.items &&
        data.listCompanyProfiles.items.length > 0
      ) {
        const companyData = data.listCompanyProfiles.items[0];
        console.log("JobDetailsList - Found company data:", companyData.companyName);
        
        // Store in localStorage (same as CompanyHome.js)
        localStorage.setItem("CompanyProfileID", companyData.id);
        localStorage.setItem("CompanyEmailID", companyData.email);
        localStorage.setItem("CompanyProfile", JSON.stringify(companyData));
        
        console.log("JobDetailsList - Stored in localStorage:", {
          CompanyProfileID: companyData.id,
          CompanyEmailID: companyData.email,
          CompanyName: companyData.companyName
        });
        
        // Update local state
        setCompany(companyData);
        setCompanyName(companyData.companyName || "");
        setCompanyemail(companyData.email || "");
        setPoint(companyData.remainingCredit || 0);
        setCompanyID(companyData.id || "");
        
        // Now fetch job details
        await fetchJobDetails(companyData.id);
      } else {
        console.warn("JobDetailsList - No company profile found for email:", emailToUse);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("JobDetailsList - Error fetching company data:", error);
      setIsLoading(false);
    }
  };

  const fetchJobDetails = async (companyProfileID) => {
    console.log("Fetching job details for company:", companyProfileID);
    try {
      let nextToken = null;
      const allJobDetails = [];

      // Fetch data with pagination
      do {
        const response = await API.graphql(
          graphqlOperation(listCompanyJobDescriptions, {
            filter: { companyProfileID: { eq: companyProfileID } },
            limit: 100, // Adjust the limit as needed
            nextToken: nextToken,
          })
        );
        const { items, nextToken: newToken } =
          response.data.listCompanyJobDescriptions;
        allJobDetails.push(...items);
        nextToken = newToken;
        console.log("Fetched job details batch:", items.length);
      } while (nextToken);

      console.log("Total job details fetched:", allJobDetails.length);
      setJobDetails(allJobDetails);
      
      // Add loading delay for transition effect
      setTimeout(() => {
        setIsLoading(false);
      }, 800);
    } catch (error) {
      console.error("Error fetching job details:", error);
      setIsLoading(false);
    }
  };

  const handleViewAndScreen = (job) => {
    setSelectedJob(job);
    setShowUnifiedModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setShowUnifiedModal(false);
    setSelectedJob(null);
    setIsEditMode(false);
    setShowPrivateScreeningModal(false);
  };

  const handleEditJob = () => {
    setShowUnifiedModal(false); // Close View & Screen modal first
    setIsEditMode(true);
    // Use setTimeout to ensure the previous modal closes before opening edit modal
    setTimeout(() => {
      setShowModal(true);
    }, 50);
  };
  
  const handlePrivateScreeningMailSubmitWithJob = async (e, job, candidateName, candidateEmail, resumeFile = null) => {
    if (CompanyEmailID !== "hr@varahitechnologies.com" && point < 200) {
      alert("You need at least 200 points to send a mail and Buy Premium.");
      handleCloseModal();
      setIsModalOpen(true);
      return;
    }

    if (resumeFile) {
      const isPdf = resumeFile.type === 'application/pdf' ||
        resumeFile.name.toLowerCase().endsWith('.pdf');
      if (!isPdf) {
        setToast({ show: true, message: '❌ Resume must be a PDF', type: 'error' });
        setTimeout(() => setToast({ show: false, message: '', type: 'error' }), 5000);
        return;
      }
      if (resumeFile.size > MAX_RESUME_SIZE_BYTES) {
        setToast({ show: true, message: '❌ Resume must be 10 MB or smaller', type: 'error' });
        setTimeout(() => setToast({ show: false, message: '', type: 'error' }), 5000);
        return;
      }
    }

    setIsSending(true);

    try {
      console.log('🏢 Company Portal: Starting private screening process');

      // Step 1: Find or create candidate profile
      const {
        candidateProfile,
        candidateProfileID,
        isExistingCandidate
      } = await findOrCreateCandidateProfile(candidateName, candidateEmail);

      console.log('✅ Candidate profile resolved:', {
        id: candidateProfileID,
        existing: isExistingCandidate,
        name: candidateProfile.fullName,
        email: candidateProfile.email
      });

      // Step 1b: If HR attached a resume, send it to the parser/storage endpoint
      // and persist parsed fields + resumeURL on the candidate profile so the bot
      // can ask resume-based questions (projects, academics, certifications).
      if (resumeFile) {
        try {
          const resumeFormData = new FormData();
          resumeFormData.append("resume", resumeFile);

          const { data: parsed = {} } = await axios.post(
            RESUME_PARSER_URL,
            resumeFormData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );

          const returnedResumeURL =
            parsed.resumeURL || parsed.resume_url ||
            parsed.url || parsed.fileURL || parsed.file_url || null;

          const profileUpdates = { id: candidateProfileID };
          if (returnedResumeURL) profileUpdates.resumeURL = returnedResumeURL;
          if (parsed.keySkills) profileUpdates.keySkills = parsed.keySkills;
          if (parsed.description) profileUpdates.description = parsed.description;
          if (parsed.currentCity) profileUpdates.currentCity = parsed.currentCity;
          if (parsed.phonenumber) profileUpdates.mobileNumber = parsed.phonenumber;
          if (parsed.address) profileUpdates.address = parsed.address;
          if (parsed.github) profileUpdates.githubURL = parsed.github;
          if (parsed.linkedin) profileUpdates.linkedinURL = parsed.linkedin;
          if (parsed.educationDetails) profileUpdates.educationDetails = parsed.educationDetails;
          else if (parsed.education) profileUpdates.educationDetails = parsed.education;
          if (parsed.roleappliedfor && parsed.roleappliedfor !== "Not specified") {
            profileUpdates.roleApplyFor = parsed.roleappliedfor;
          }
          if (parsed.workstatus && parsed.workstatus !== "Not specified") {
            profileUpdates.workStatus = parsed.workstatus;
          }

          if (Object.keys(profileUpdates).length > 1) {
            await API.graphql(
              graphqlOperation(updateCandidateProfile, { input: profileUpdates })
            );
            console.log('📎 Resume processed and candidate profile enriched');
          }
        } catch (resumeError) {
          console.error('❌ Resume upload/parse failed (continuing with invite):', resumeError);
          setToast({ show: true, message: '⚠️ Resume upload failed; sending invite without resume', type: 'error' });
          setTimeout(() => setToast({ show: false, message: '', type: 'error' }), 5000);
        }
      }

      // Step 2: Create interview slot with meeting config
      let interviewLink = null;
      try {
        const interviewSlotInput = {
          candidateProfileID: candidateProfileID,
          candidateEmail: candidateProfile.email,
          date: new Date().toISOString().split('T')[0],
          startTime: '09:00',
          endTime: '10:00',
          status: TOKEN_STATUS.ACTIVE,
          meetingLinkURL: '',
          meetingConfig: createMeetingConfig({
            companyID: companyID || 'unknown',
            companyName: company?.companyName || 'Company',
            jobTitle: job.jobTitle,
            jdID: job.id,
            interviewType: 'company-portal',
            candidateName: candidateName
          }),
          candidateAttendance: false,
          botAttendance: false,
          jdID: job.id,
          candidateProfileInterviewSlotsId: candidateProfileID
        };

        const createResponse = await API.graphql(
          graphqlOperation(createInterviewSlot, { input: interviewSlotInput })
        );

        if (createResponse.data && createResponse.data.createInterviewSlot) {
          const interviewSlotId = createResponse.data.createInterviewSlot.id;
          interviewLink = createPublicInterviewUrl(interviewSlotId);
          console.log('🔗 Generated interview link:', interviewLink);
        }
      } catch (linkError) {
        console.error('❌ Error generating interview link:', linkError);
      }

      // Step 3: Send invitation email
      const emailSent = await sendPrivateScreeningInvitationEmailWithJob(
        candidateName,
        candidateEmail,
        interviewLink,
        job
      );

      if (emailSent) {
        await createPrivateScreeningInvitation(candidateName, candidateEmail);

        // Step 4: Update SpotJD and Knowledge Graph
        try {
          const spotJD = await getSpotJDByJobDescriptionId(job.id);
          
          if (spotJD) {
            await addCandidateToSpotJDSelected(spotJD.id, spotJD.selectedCandidate, candidateEmail);
            await createCandidateStatusRecord(spotJD.id, candidateEmail, 'selected');
            await selectCandidateInGraph(spotJD.id, candidateEmail, 1, 'Selected via private screening email');
            console.log('✅ SpotJD and Knowledge Graph updated successfully');
          }
        } catch (updateError) {
          console.error('❌ Error updating SpotJD/Knowledge Graph:', updateError);
        }

        // Show success toast
        setToast({ show: true, message: `✅ Invitation sent to ${candidateEmail}`, type: 'success' });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);

        // Refresh candidate list
        await fetchCandidatesForJob(job.id);
        
        console.log("✅ Email sent and private screening created successfully");
        e.target.reset();
      } else {
        setToast({ show: true, message: '❌ Failed to send email', type: 'error' });
        setTimeout(() => setToast({ show: false, message: '', type: 'error' }), 4000);
      }
    } catch (error) {
      console.error("❌ Error creating private screening:", error);
      setToast({ show: true, message: '❌ Error sending invitation', type: 'error' });
      setTimeout(() => setToast({ show: false, message: '', type: 'error' }), 4000);
    } finally {
      setIsSending(false);
    }
  };
  
  // Open the reinvite confirmation modal (or short-circuit to the BuyPremium
  // modal if the company doesn't have enough credits).
  const handleReinviteCandidate = (candidate, job) => {
    if (!candidate || !candidate.candidateEmail || !job) return;

    if (CompanyEmailID !== "hr@varahitechnologies.com" && point < 200) {
      setIsModalOpen(true);
      return;
    }

    setReinviteTarget({ candidate, job });
  };

  // Actually send the reinvite — runs after the user confirms in the modal.
  // Mints a fresh InterviewSlot, emails the new link, deducts credits the same
  // way the original invite flow does.
  const confirmReinviteCandidate = async () => {
    if (!reinviteTarget) return;
    const { candidate, job } = reinviteTarget;
    setReinviteTarget(null);

    setReinvitingId(candidate.id);
    // createPrivateScreeningInvitation reads selectedJob, so set it before charging.
    setSelectedJob(job);

    try {
      const {
        candidateProfile,
        candidateProfileID
      } = await findOrCreateCandidateProfile(candidate.candidateName, candidate.candidateEmail);

      let interviewLink = null;
      try {
        const interviewSlotInput = {
          candidateProfileID: candidateProfileID,
          candidateEmail: candidateProfile.email,
          date: new Date().toISOString().split('T')[0],
          startTime: '09:00',
          endTime: '10:00',
          status: TOKEN_STATUS.ACTIVE,
          meetingLinkURL: '',
          meetingConfig: createMeetingConfig({
            companyID: companyID || 'unknown',
            companyName: company?.companyName || 'Company',
            jobTitle: job.jobTitle,
            jdID: job.id,
            interviewType: 'company-portal',
            candidateName: candidate.candidateName
          }),
          candidateAttendance: false,
          botAttendance: false,
          jdID: job.id,
          candidateProfileInterviewSlotsId: candidateProfileID
        };

        const createResponse = await API.graphql(
          graphqlOperation(createInterviewSlot, { input: interviewSlotInput })
        );

        if (createResponse.data && createResponse.data.createInterviewSlot) {
          const newSlot = createResponse.data.createInterviewSlot;
          interviewLink = createPublicInterviewUrl(newSlot.id);

          // Invalidate any prior live slots for this candidate+job so the
          // previous interview link stops working. PublicInterview.js rejects
          // slots with status USED or EXPIRED (see PublicInterview.js:72-78),
          // so flipping to EXPIRED is enough to lock the old link out.
          // COMPLETED slots are left alone — that's history, not a live link.
          try {
            const normalizedEmail = (candidateProfile.email || '').trim().toLowerCase();
            let priorSlots = [];
            let priorToken = null;
            do {
              const priorResp = await API.graphql(
                graphqlOperation(listInterviewSlots, {
                  filter: { jdID: { eq: job.id } },
                  limit: 100,
                  nextToken: priorToken,
                })
              );
              priorSlots.push(...priorResp.data.listInterviewSlots.items);
              priorToken = priorResp.data.listInterviewSlots.nextToken;
            } while (priorToken);

            const toExpire = priorSlots.filter((s) =>
              s.id !== newSlot.id &&
              (s.candidateEmail || '').trim().toLowerCase() === normalizedEmail &&
              s.status !== TOKEN_STATUS.USED &&
              s.status !== TOKEN_STATUS.EXPIRED
            );

            if (toExpire.length > 0) {
              await Promise.allSettled(
                toExpire.map((s) =>
                  API.graphql(graphqlOperation(updateInterviewSlot, {
                    input: { id: s.id, status: TOKEN_STATUS.EXPIRED }
                  }))
                )
              );
              console.log(`🔒 Reinvite: expired ${toExpire.length} prior slot(s) for ${normalizedEmail}`);
            }
          } catch (expireErr) {
            // Non-fatal: the new link still works either way. Worst case the
            // old link also still works until the next reinvite.
            console.warn('⚠️ Reinvite: failed to expire prior slots:', expireErr);
          }
        }
      } catch (linkError) {
        console.error('❌ Reinvite: error generating new interview slot:', linkError);
      }

      const emailSent = await sendPrivateScreeningInvitationEmailWithJob(
        candidate.candidateName,
        candidate.candidateEmail,
        interviewLink,
        job
      );

      if (emailSent) {
        await createPrivateScreeningInvitation(candidate.candidateName, candidate.candidateEmail);

        setToast({ show: true, message: `✅ Reinvitation sent to ${candidate.candidateEmail}`, type: 'success' });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);

        await fetchCandidatesForJob(job.id);
      } else {
        setToast({ show: true, message: '❌ Failed to send reinvitation', type: 'error' });
        setTimeout(() => setToast({ show: false, message: '', type: 'error' }), 4000);
      }
    } catch (error) {
      console.error('❌ Reinvite error:', error);
      setToast({ show: true, message: '❌ Error sending reinvitation', type: 'error' });
      setTimeout(() => setToast({ show: false, message: '', type: 'error' }), 4000);
    } finally {
      setReinvitingId(null);
    }
  };

  const handlePrivateScreeningMailSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission behavior

    if (CompanyEmailID !== "hr@varahitechnologies.com" && point < 200) {
      alert("You need at least 200 points to send a mail and Buy Premium.");
      handleCloseModal();
      setIsModalOpen(true); // Open modal if point is less than 200
      console.log(`isModalOpen`, isModalOpen); // Log the state of isModalOpen
      return; // Exit the function
    }

    // If point is 200 or greater, proceed to extract candidate info from the form
    const candidateName = e.target.elements.candidateName.value;
    const candidateEmail = e.target.elements.candidateEmail.value;
    
    try {
      console.log('🏢 Company Portal: Starting private screening process');
      
      // Step 1: Find or create candidate profile using shared utility
      const {
        candidateProfile,
        candidateProfileID,
        isExistingCandidate
      } = await findOrCreateCandidateProfile(candidateName, candidateEmail);

      console.log('✅ Candidate profile resolved:', {
        id: candidateProfileID,
        existing: isExistingCandidate,
        name: candidateProfile.fullName,
        email: candidateProfile.email
      });

      // Step 2: Create interview slot with simplified meeting config
      let interviewLink = null;
      try {
        const interviewSlotInput = {
          candidateProfileID: candidateProfileID,
          candidateEmail: candidateProfile.email,
          date: new Date().toISOString().split('T')[0], // Today's date
          startTime: '09:00',
          endTime: '10:00',
          status: TOKEN_STATUS.ACTIVE, // Use SCHEDULED status for active interviews
          meetingLinkURL: '', // This can be empty for now
          meetingConfig: createMeetingConfig({
            companyID: companyID || 'unknown',
            companyName: company?.companyName || 'Company',
            jobTitle: selectedJob.jobTitle,
            jdID: selectedJob.id,
            interviewType: 'company-portal',
            candidateName: candidateName
          }),
          candidateAttendance: false,
          botAttendance: false,
          jdID: selectedJob.id, // Use the actual job ID
          candidateProfileInterviewSlotsId: candidateProfileID // Establish relationship
        };

        console.log('📝 Creating interview slot with input:', interviewSlotInput);

        // Create the interview slot
        const createResponse = await API.graphql(
          graphqlOperation(createInterviewSlot, { input: interviewSlotInput })
        );

        console.log('📋 Interview slot creation response:', createResponse);

        if (createResponse.data && createResponse.data.createInterviewSlot) {
          // Generate the public link using interview slot ID as token
          const interviewSlotId = createResponse.data.createInterviewSlot.id;
          interviewLink = createPublicInterviewUrl(interviewSlotId);
          console.log('🔗 Generated interview link:', interviewLink);
        } else {
          console.error('❌ Failed to create interview slot - no data returned');
        }
      } catch (linkError) {
        console.error('❌ Error generating interview link:', linkError);
        console.error('GraphQL Errors:', linkError.errors);
        // Continue with email without link if generation fails
      }

      // Step 3: Send invitation email (with or without interview link)
      const emailSent = await sendPrivateScreeningInvitationEmail(
        candidateName,
        candidateEmail,
        interviewLink // Pass the interview link to email function
      );

      if (emailSent) {
        // If email sent successfully, create private screening invitation
        await createPrivateScreeningInvitation(candidateName, candidateEmail);

        // Step 4: Update SpotJD and Knowledge Graph for the selected candidate
        console.log('🔄 Updating SpotJD and Knowledge Graph...');
        try {
          // Get the SpotJD associated with this job description
          const spotJD = await getSpotJDByJobDescriptionId(selectedJob.id);
          
          if (spotJD) {
            console.log('📋 Found SpotJD:', spotJD.id);
            
            // Add candidate to SpotJD's selectedCandidate array
            await addCandidateToSpotJDSelected(spotJD.id, spotJD.selectedCandidate, candidateEmail);
            
            // Create SpotCandidateStatus record
            await createCandidateStatusRecord(spotJD.id, candidateEmail, 'selected');
            
            // Update the knowledge graph with selected candidate
            await selectCandidateInGraph(spotJD.id, candidateEmail, 1, 'Selected via private screening email');
            
            console.log('✅ SpotJD and Knowledge Graph updated successfully');
          } else {
            console.warn('⚠️ No SpotJD found for job description:', selectedJob.id);
          }
        } catch (updateError) {
          console.error('❌ Error updating SpotJD/Knowledge Graph:', updateError);
          // Don't block the flow if this fails
        }

        console.log("✅ Email sent and private screening created successfully");
        e.target.reset(); // Reset the form
      } else {
        console.error("❌ Email sending failed, skipping further steps.");
      }
    } catch (error) {
      console.error("❌ Error creating private screening:", error);
    }
  };

  //   const sendPrivateScreeningInvitationEmail = async (
  //     candidateName,
  //     candidateEmail
  //   ) => {
  //     const sesClient = new SESClient({
  //       region: "ap-south-1",
  //       credentials: {
  //         accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  //         secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  //       },
  //       // credentials: {
  //       //   accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  //       //   secretAccessKey: process.env.EACT_APP_AWS_SECRET_ACCESS_KEY,
  //       // },
  //     });

  //     const emailParams = {
  //       Source: "getscreened@varahitechnologies.com",
  //       Destination: {
  //         ToAddresses: [candidateEmail],
  //       },
  //       Message: {
  //         Subject: {
  //           Data: `Congratulations! You've been selected for the screening round for the position of ${selectedJob.jobTitle} at ${company.companyName}.`,
  //         },
  //         Body: {
  //           Text: {
  //             Data: `Dear ${candidateName},

  // Greetings of the Day!
  // I am delighted to inform you that you have been selected for further consideration for the position of ${selectedJob.jobTitle} at ${company.companyName}. Here are some details about the job:

  // Job Title: ${selectedJob.jobTitle}
  // Category: ${selectedJob.selectedCategory}
  // Experience Required: ${selectedJob.selectedExperience}
  // Compensation: ${selectedJob.compensation}
  // Benefits: ${selectedJob.benefits}
  // Skills Required: ${selectedJob.skill}
  // Job Description:
  // ${selectedJob.jobDescription}

  // To streamline the process, we have set up an online portal for the interview. You can access the interview portal using the following link: https://www.getscreened.in. We have also provided a YouTube video for your reference: https://www.youtube.com/watch?si=r94pgS7GIIy-p6j-&v=3rMe3qx9Amc&feature=youtu.be.
  // Please select a suitable time slot from the available options in the portal. If none of the provided time work for you,  we will do our best to accommodate your schedule, and will reach out to you shortly to schedule the interview. Please let us know if you are available for a private screening interview.

  // Best regards,
  //  ${company.contactPersonDesignation}
  //  ${company.contactPersonName}
  //  ${company.contactPersonNumber}
  //  ${company.email}
  //  ${company.companyUrl}`,
  //           },
  //         },
  //       },
  //     };

  //     const sendEmailCommand = new SendEmailCommand(emailParams);
  //     await sesClient.send(sendEmailCommand);
  //     handleCloseModal();
  //   };
  
  // Shared email-body builder for private-screening invites (used by both the
  // initial-invite and reinvite flows). Kept in one place so the templates
  // don't drift the next time the format is tweaked.
  const buildPrivateScreeningEmailHtml = (candidateName, job, companyData, interviewLink) => {
    const jobDescHtml = (job?.jobDescription || '').replace(/\n/g, '<br>');
    const detailRow = (label, value) => (value
      ? `<tr>
          <td style="padding:10px 16px 10px 0;color:#64748b;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;white-space:nowrap;vertical-align:top;width:130px;border-bottom:1px solid #f1f5f9;">${label}</td>
          <td style="padding:10px 0;color:#0f172a;font-size:14px;font-weight:500;vertical-align:top;border-bottom:1px solid #f1f5f9;">${value}</td>
        </tr>`
      : '');

    const ctaBlock = interviewLink ? `
      <tr>
        <td style="padding:8px 32px 28px 32px;text-align:center;">
          <table cellpadding="0" cellspacing="0" border="0" style="margin:0 auto;">
            <tr>
              <td style="background:#1e293b;border-radius:4px;">
                <a href="${interviewLink}" style="display:inline-block;color:#ffffff;text-decoration:none;padding:14px 40px;font-size:15px;font-weight:600;letter-spacing:0.3px;">Begin Your Interview &rarr;</a>
              </td>
            </tr>
          </table>
          <p style="margin:14px 0 0 0;font-size:12px;color:#64748b;line-height:1.5;">
            <strong style="color:#0f172a;">Secure &amp; Private:</strong> This link is unique to you and expires after a single use.
          </p>
        </td>
      </tr>` : '';

    const fallbackNote = interviewLink
      ? ''
      : `<p style="margin:14px 0 0 0;font-size:13px;color:#475569;line-height:1.6;">For scheduling questions, please reply to this email.</p>`;

    return `
<div style="background:#eef0f3;padding:32px 12px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#0f172a;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:640px;margin:0 auto;background:#ffffff;border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;">
    <tr>
      <td style="background:#0f172a;padding:24px 32px;border-bottom:3px solid #4f46e5;">
        <table width="100%" cellpadding="0" cellspacing="0" border="0">
          <tr>
            <td style="vertical-align:middle;">
              <p style="margin:0;color:#94a3b8;font-size:11px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;">Interview Invitation</p>
              <h1 style="margin:4px 0 0 0;color:#ffffff;font-size:20px;font-weight:600;letter-spacing:-0.2px;">${companyData?.companyName || 'Company'}</h1>
            </td>
            <td style="vertical-align:middle;text-align:right;">
              <p style="margin:0;color:#94a3b8;font-size:11px;font-weight:500;letter-spacing:0.3px;">CONFIDENTIAL</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>

    <tr>
      <td style="padding:32px 32px 8px 32px;">
        <p style="margin:0 0 18px 0;font-size:15px;color:#0f172a;font-weight:500;">Dear ${candidateName},</p>
        <p style="margin:0 0 16px 0;font-size:14px;line-height:1.7;color:#334155;">
          We are pleased to inform you that, following a review of your profile, you have been <strong style="color:#0f172a;">shortlisted</strong> for the screening round for the position of <strong style="color:#4f46e5;">${job?.jobTitle || ''}</strong> at <strong style="color:#0f172a;">${companyData?.companyName || ''}</strong>.
        </p>
        <p style="margin:0 0 22px 0;font-size:14px;line-height:1.7;color:#334155;">
          Kindly review the position details below and proceed to the interview portal at your earliest convenience.
        </p>
      </td>
    </tr>

    <tr>
      <td style="padding:0 32px 24px 32px;">
        <p style="margin:0 0 12px 0;font-size:11px;font-weight:700;color:#4f46e5;letter-spacing:1.2px;text-transform:uppercase;">&#9632;&nbsp; Position Summary</p>
        <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:4px;">
          <tr>
            <td style="padding:6px 20px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                ${detailRow('Job Title', job?.jobTitle)}
                ${detailRow('Category', job?.selectedCategory)}
                ${detailRow('Experience', job?.selectedExperience)}
                ${detailRow('Key Skills', job?.skill)}
                ${detailRow('Compensation', job?.compensation)}
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>

    ${(job?.benefits || jobDescHtml) ? `
    <tr>
      <td style="padding:0 32px 24px 32px;">
        <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:4px;padding:20px 22px;">
          ${jobDescHtml ? `
            <p style="margin:0 0 10px 0;font-size:11px;font-weight:700;color:#4f46e5;letter-spacing:1.2px;text-transform:uppercase;">Job Description</p>
            <div style="font-size:14px;line-height:1.7;color:#334155;${job?.benefits ? 'margin-bottom:20px;padding-bottom:18px;border-bottom:1px solid #e2e8f0;' : ''}">${jobDescHtml}</div>
          ` : ''}
          ${job?.benefits ? `
            <p style="margin:0 0 10px 0;font-size:11px;font-weight:700;color:#0284c7;letter-spacing:1.2px;text-transform:uppercase;">Benefits</p>
            <p style="margin:0;font-size:14px;line-height:1.7;color:#334155;">${job.benefits}</p>
          ` : ''}
        </div>
      </td>
    </tr>` : ''}

    ${interviewLink ? `
    <tr>
      <td style="padding:4px 32px 8px 32px;">
        <p style="margin:0 0 14px 0;font-size:11px;font-weight:700;color:#4f46e5;letter-spacing:1.2px;text-transform:uppercase;">&#9632;&nbsp; Next Step</p>
        <p style="margin:0 0 4px 0;font-size:14px;line-height:1.7;color:#334155;">
          Your interview is <strong style="color:#059669;">ready to begin</strong>. The session is conducted online and takes approximately <strong style="color:#0f172a;">20&ndash;30 minutes</strong>. <strong>No prior registration is required.</strong>
        </p>
      </td>
    </tr>
    ` : ''}
    ${ctaBlock}

    <tr>
      <td style="padding:0 32px 8px 32px;">
        <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#fffbeb;border-left:3px solid #f59e0b;border-radius:4px;">
          <tr>
            <td style="padding:12px 18px;">
              <p style="margin:0;font-size:13px;line-height:1.6;color:#78350f;">
                <strong>Need help?</strong> Watch our
                <a href="https://www.youtube.com/watch?si=r94pgS7GIIy-p6j-&v=3rMe3qx9Amc&feature=youtu.be" style="color:#b45309;text-decoration:underline;font-weight:600;">brief introduction video</a>
                to learn how the interview process works.
              </p>
            </td>
          </tr>
        </table>
        ${fallbackNote}
      </td>
    </tr>

    <tr>
      <td style="padding:24px 32px 28px 32px;border-top:1px solid #e2e8f0;">
        <p style="margin:8px 0 12px 0;font-size:13px;color:#475569;">We look forward to speaking with you.</p>
        <p style="margin:0 0 4px 0;font-size:13px;color:#475569;font-weight:500;">Sincerely,</p>
        <table cellpadding="0" cellspacing="0" border="0" style="margin-top:8px;">
          <tr>
            <td style="border-left:3px solid #4f46e5;padding:2px 0 2px 14px;">
              <p style="margin:0 0 2px 0;font-size:15px;color:#0f172a;font-weight:700;">${companyData?.contactPersonName || ''}</p>
              ${companyData?.contactPersonDesignation ? `<p style="margin:0 0 6px 0;font-size:12px;color:#64748b;font-weight:500;">${companyData.contactPersonDesignation}${companyData?.companyName ? ` &middot; <span style="color:#0f172a;">${companyData.companyName}</span>` : ''}</p>` : ''}
              ${companyData?.contactPersonNumber ? `<p style="margin:0;font-size:12px;color:#475569;">&#9742;&nbsp; ${companyData.contactPersonNumber}</p>` : ''}
              ${companyData?.email ? `<p style="margin:2px 0 0 0;font-size:12px;"><a href="mailto:${companyData.email}" style="color:#4f46e5;text-decoration:none;font-weight:500;">&#9993;&nbsp; ${companyData.email}</a></p>` : ''}
              ${companyData?.companyUrl ? `<p style="margin:2px 0 0 0;font-size:12px;"><a href="${companyData.companyUrl}" style="color:#4f46e5;text-decoration:none;font-weight:500;">&#127760;&nbsp; ${companyData.companyUrl}</a></p>` : ''}
            </td>
          </tr>
        </table>
      </td>
    </tr>

    <tr>
      <td style="background:#0f172a;padding:14px 32px;text-align:center;">
        <p style="margin:0;font-size:11px;color:#94a3b8;letter-spacing:0.4px;">
          Securely delivered via <strong style="color:#ffffff;">GetScreened</strong> &middot; AI-Powered Interview Platform
        </p>
      </td>
    </tr>
  </table>

  <p style="max-width:640px;margin:14px auto 0 auto;text-align:center;font-size:11px;color:#94a3b8;line-height:1.5;">
    This email is intended solely for ${candidateName}. If you received it in error, please disregard.
  </p>
</div>`;
  };

  const sendPrivateScreeningInvitationEmailWithJob = async (candidateName, candidateEmail, interviewLink = null, job) => {
    const subject = `Interview Invitation: ${job.jobTitle} at ${company.companyName}`;
    const emailBody = buildPrivateScreeningEmailHtml(candidateName, job, company, interviewLink);

    const myHeaders = new Headers();
    myHeaders.append("X-Api-Key", "abce-124");
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      subject: subject,
      body: emailBody,
      to: candidateEmail,
      fromName: "GetScreened",
      fromEmail: "support@getscreened.in",
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    try {
      const response = await fetch(
        "https://pharmaclick.co.in/RazorPay/WeatherForecast/sendEmailSES",
        requestOptions
      );
      const result = await response.text();
      console.log(result);

      // Credit deduction is intentionally NOT done here. createPrivateScreeningInvitation
      // is the single source of truth for charging — calling updateCompanyPoint() here
      // too would double-charge the company (200 wallet hit + 200 = 400 per invite).
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const sendPrivateScreeningInvitationEmail = async (candidateName, candidateEmail, interviewLink = null) => {
    setIsSending(true);
    const subject = `Interview Invitation: ${selectedJob.jobTitle} at ${company.companyName}`;
    const emailBody = buildPrivateScreeningEmailHtml(candidateName, selectedJob, company, interviewLink);

    const myHeaders = new Headers();
    // myHeaders.append("X-Api-Key", process.env.REACT_APP_API_KEY);
    myHeaders.append("X-Api-Key", "abce-124");
    myHeaders.append("Content-Type", "application/json");    const raw = JSON.stringify({
      subject: subject,
      body: emailBody,
      to: candidateEmail,
      fromEmail: company.email,
      fromName: company.contactPersonName,
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    try {
      const response = await fetch(
        "https://pharmaclick.co.in/RazorPay/WeatherForecast/sendEmailSES",
        requestOptions
      );
      const result = await response.json(); // Parse the response as JSON

      if (result.message === "Invalid API Key") {
        console.error("Invalid API Key");
        return false;
      }

      if (
        result.message ===
        "The SMTP server requires a secure connection or the client was not authenticated. The server response was: 5.7.0 Authentication Required. For more information, go to"
      ) {
        console.log("SMTP Problem");
        return false;
      }

      console.log(result);
      handleCloseModal();
      return true;
    } catch (error) {
      console.error("Error sending email:", error);
      return false;
    } finally {
      setIsSending(false);
    }
  };

  const createPrivateScreeningInvitation = async (
    candidateName,
    candidateEmail
  ) => {
    try {
      // Update company point
      const updateSuccess = await updateCompanyPoint();

      if (updateSuccess) {
        // Proceed with creating the private screening

        // Call createPrivateScreening mutation
        const createPrivateScreeningInput = {
          companyProfileID: companyID,
          jobDescription: selectedJob.jobTitle,
          candidateName: candidateName,
          candidateEmail: candidateEmail,
          jdID: selectedJob.id,
          pointsDeducted: 200,
        };

        const createPrivateScreeningResult = await API.graphql(
          graphqlOperation(createPrivateScreening, {
            input: createPrivateScreeningInput,
          })
        );

        if (createPrivateScreeningResult.data.createPrivateScreening) {
          console.log("Private screening created successfully");
        } else {
          console.error("Failed to create private screening");
        }
      } else {
        console.error("Failed to update company point");
      }
    } catch (error) {
      console.error("Error creating private screening:", error);
      throw error; // Rethrow the error to be handled in handlePrivateScreeningMailSubmit
    }
  };
  const updateCompanyPoint = async () => {
    try {
      // Declare variables
      let candidateProfileEmailID, data, companyData, items, newNextToken;

      // Fetch candidate profile email ID from local storage
      candidateProfileEmailID = localStorage.getItem("candidateProfileEmailID");
      setCompanyemail(candidateProfileEmailID);

      // Check if candidate email is available
      if (!candidateProfileEmailID) {
        // Handle case where candidate email is not found
        return false;
      }

      let currentRemainingCredit = point;
      let updatedRemainingCredit = currentRemainingCredit - 200;

      setPoint(updatedRemainingCredit);

      const updateInput = {
        id: company.id, // Assuming you have the company ID available
        remainingCredit: updatedRemainingCredit,
      };

      // Fetch company profiles data
      const result = await API.graphql(
        graphqlOperation(updateCompanyProfile, {
          input: updateInput,
        })
      );

      console.log("GraphQL Result:", result);

      // Destructure data from result
      data = result.data;

      // Check if data is available
      if (data && data.updateCompanyProfile) {
        // Extract company data
        companyData = data.updateCompanyProfile;
        setCompany(companyData);
        // Keep the cached profile in sync with the backend, otherwise the
        // next page load will hydrate `point` from the stale cached
        // remainingCredit and the deduction will appear to "revert".
        localStorage.setItem("CompanyProfile", JSON.stringify(companyData));
        // DON'T overwrite the credit - we already set it correctly above
        // setPoint(companyData.remainingCredit); // REMOVED - this was causing the fluctuation!

        console.log("Company Data:", companyData);
        console.log("Credit successfully updated to:", updatedRemainingCredit);

        setLoading(false);
        return true; // Return true for success
      } else {
        console.error("No data returned or invalid response structure:", data);
        setLoading(false); // Set loading to false if no data is found
        return false; // Return false for failure
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      // Handle error
      setLoading(false); // Set loading to false on error
      return false; // Return false for failure
    }
  };
  const handleDeleteJob = async (jobId, jobTitle) => {
    const isConfirmed = window.confirm(
      `Are you sure you want to delete the job titled "${jobTitle}"?`
    );
    if (!isConfirmed) {
      return; // Exit the function if the user cancels the deletion
    }

    try {
      await API.graphql(
        graphqlOperation(deleteCompanyJobDescription, { input: { id: jobId } })
      );
      setJobDetails((prevJobDetails) =>
        prevJobDetails.filter((job) => job.id !== jobId)
      );
      console.log("Job deleted successfully");
    } catch (error) {
      console.error("Error deleting job:", error);
    }
  };

  // Sign-in-less interview functions
  const handleSignInLessInterview = (job) => {
    setSelectedJob(job);
    setShowSignInLessModal(true);
  };
  const generateSignInLessLink = async (candidateName, candidateEmail) => {
    if (!candidateName || !candidateEmail) {
      setLinkGenerationError('Please provide both candidate name and email');
      return;
    }

    setIsGeneratingLink(true);
    setLinkGenerationError('');

    try {
      console.log('🔗 Sign-in-less: Starting link generation process');
      
      // Step 1: Find or create candidate profile using shared utility
      const {
        candidateProfile,
        candidateProfileID,
        isExistingCandidate
      } = await findOrCreateCandidateProfile(candidateName, candidateEmail);

      console.log('✅ Candidate profile resolved for sign-in-less:', {
        id: candidateProfileID,
        existing: isExistingCandidate,
        name: candidateProfile.fullName,
        email: candidateProfile.email,
        userID: candidateProfile.userID
      });      // Step 2: Create interview slot with simplified meeting config
      const interviewSlotInput = {
        candidateProfileID: candidateProfileID,
        candidateEmail: candidateProfile.email,
        date: new Date().toISOString().split('T')[0], // Today's date
        startTime: '09:00',
        endTime: '10:00',
        status: TOKEN_STATUS.ACTIVE,
        meetingLinkURL: '', // This can be empty for now
        meetingConfig: createMeetingConfig({
          companyID: companyID || 'unknown',
          companyName: company?.companyName || 'Company',
          jobTitle: selectedJob?.jobTitle || 'Position',
          jdID: selectedJob?.id,
          interviewType: 'sign-in-less',
          candidateName: candidateName
        }),
        candidateAttendance: false,
        botAttendance: false,
        jdID: selectedJob.id, // Use the actual job ID
        candidateProfileInterviewSlotsId: candidateProfileID // Establish relationship
      };

      console.log('📝 Creating interview slot for sign-in-less with input:', interviewSlotInput);

      // Create the interview slot
      const createResponse = await API.graphql(
        graphqlOperation(createInterviewSlot, { input: interviewSlotInput })
      );

      console.log('📋 Interview slot creation response:', createResponse);

      if (createResponse.data.createInterviewSlot) {
        // Use the generated ID as the access token
        const interviewSlotId = createResponse.data.createInterviewSlot.id;
        const publicLink = createPublicInterviewUrl(interviewSlotId);
        
        console.log('🎉 Successfully created sign-in-less interview slot:', {
          slotId: interviewSlotId,
          candidateProfileID: candidateProfileID,
          candidateEmail: candidateProfile.email,
          isExistingCandidate: isExistingCandidate,
          publicLink: publicLink
        });
        
        setGeneratedLink(publicLink);
        setShowLinkModal(true);
        setShowSignInLessModal(false);
      } else {
        console.error('❌ Failed to create interview slot - no data returned');
        setLinkGenerationError('Failed to create interview slot');
      }    } catch (error) {
      console.error('Error generating sign-in-less link:', error);
      // Log the actual error for debugging
      if (error.errors && error.errors.length > 0) {
        console.error('GraphQL Errors:', error.errors);
        setLinkGenerationError(`Error: ${error.errors[0].message}`);
      } else if (error.data && error.data.errors) {
        console.error('GraphQL Errors in data:', error.data.errors);
        setLinkGenerationError(`Error: ${error.data.errors[0].message}`);
      } else {
        setLinkGenerationError('Failed to generate interview link. Please try again.');
      }
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(generatedLink);
    alert('Link copied to clipboard!');
  };

  const closeLinkModal = () => {
    setShowLinkModal(false);
    setGeneratedLink('');
    setLinkGenerationError('');
  };

  const closeSignInLessModal = () => {
    setShowSignInLessModal(false);
    setLinkGenerationError('');
  };

  const handleSignInLessSubmit = (event) => {
    event.preventDefault();
    const candidateName = event.target.candidateName.value;
    const candidateEmail = event.target.candidateEmail.value;
    generateSignInLessLink(candidateName, candidateEmail);
  };

  // Keep the ref pointed at the latest row-action handlers every render so the
  // stable wrappers above always call the current closures.
  rowHandlersRef.current = { setVideoModalUrl, openGmailCompose, handleReinviteCandidate, viewCandidateProfile };

  // console.log("pppppppppppppppppppppppppp",company)
  return (
    <div className="min-h-screen" style={{
      fontFamily: 'Sora, Inter, sans-serif',
      background: '#0f172a',
      color: '#F9FAFB',
      position: 'relative'
    }}>
      {/* Background Effect */}
      <div className="fixed inset-0" style={{ zIndex: 0 }}>
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.4) 0%, rgba(15, 23, 42, 0.6) 100%)'
          }}
        />
      </div>

      {/* Animation keyframes */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700&display=swap');
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        @keyframes aurora {
          from {
            background-position: 50% 50%, 50% 50%;
          }
          to {
            background-position: 350% 50%, 350% 50%;
          }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0px);
          }
        }
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0px);
          }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            max-height: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            max-height: 2000px;
            transform: translateY(0);
          }
        }
        @keyframes slideUp {
          from {
            opacity: 1;
            max-height: 2000px;
            transform: translateY(0);
          }
          to {
            opacity: 0;
            max-height: 0;
            transform: translateY(-20px);
          }
        }
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.7);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 0 0 10px rgba(99, 102, 241, 0);
          }
        }
        @keyframes floatButton {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }
        @keyframes shimmer {
          0% {
            background-position: -200% center;
          }
          100% {
            background-position: 200% center;
          }
        }
        @keyframes breathe {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 4px 20px rgba(99, 102, 241, 0.15);
          }
          50% {
            transform: scale(1.02);
            box-shadow: 0 6px 30px rgba(99, 102, 241, 0.25);
          }
        }
        @keyframes slideInFromRight {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes slideInFromTop {
          from {
            transform: translateY(-100px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes slideOutToTop {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(-100px);
            opacity: 0;
          }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        .animate-slideInLeft {
          animation: slideInLeft 0.6s ease-out forwards;
        }
        .animate-scaleIn {
          animation: scaleIn 0.4s ease-out forwards;
        }
        .animate-slideDown {
          animation: slideDown 0.5s ease-out forwards;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out forwards;
        }
        .animate-pulse-glow {
          animation: pulse 2s infinite;
        }
        .animate-float {
          animation: floatButton 3s ease-in-out infinite;
        }
        .animate-breathe {
          animation: breathe 3s ease-in-out infinite;
        }
        .ai-button-shimmer {
          animation: shimmer 3s linear infinite;
        }
        .ai-button-slide-in {
          animation: slideInFromRight 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        .stagger-1 { animation-delay: 0.1s; }
        .stagger-2 { animation-delay: 0.2s; }
        .stagger-3 { animation-delay: 0.3s; }
        .stagger-4 { animation-delay: 0.4s; }
        
        /* Dashboard transition styles */
        .job-card-hidden {
          opacity: 0;
          transform: scale(0.95) translateY(-10px);
          max-height: 0;
          padding: 0;
          margin: 0;
          overflow: hidden;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .job-card-visible {
          opacity: 1;
          transform: scale(1) translateY(0);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .dashboard-enter {
          opacity: 0;
          transform: scale(0.98);
        }
        .dashboard-enter-active {
          opacity: 1;
          transform: scale(1);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        /* Job card expandable styles */
        .job-card-expandable {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .job-card-checkbox:checked ~ .job-card-expandable {
          max-height: 300px;
        }
        .job-card-checkbox:checked + .job-card-label .expand-icon {
          transform: rotate(180deg);
        }
        .expand-icon {
          transition: transform 0.3s ease;
        }
      `}</style>

      <div style={{ position: 'relative', zIndex: 100 }}>
        <Header />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1" style={{ position: 'relative', zIndex: 1 }}>
        {/* Header Section with inline Create Job & Credits */}
        <header className="mb-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h1 className="text-base font-bold text-white">Interview Management</h1>
            
            {/* Inline Quick Actions */}
            <div className="flex items-center gap-2">
              {localStorage.getItem("CompanyEmailID") !== "hr@varahitechnologies.com" && (
                <>
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg" style={{ background: 'rgba(17, 24, 39, 0.5)', border: '1px solid rgba(55, 65, 81, 1)' }}>
                    <span className="text-[10px] text-gray-400">Credits:</span>
                    <span className="text-xs font-bold text-white">{point}</span>
                    <button 
                      onClick={() => setIsModalOpen(true)}
                      className="ml-0.5 text-[10px] text-indigo-400 hover:text-indigo-300 transition-colors"
                    >
                      +Buy
                    </button>
                  </div>
                </>
              )}
              <Link to={"/JobDetails"}>
                <button 
                  className="flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-1 px-2 rounded text-xs"
                >
                  <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '14px' }}>add</span>
                  New Job
                </button>
              </Link>
            </div>
          </div>
        </header>

        {/* Main Content - Full Width Job Listings with Inline Candidates */}
        <main>
          {/* Dashboard Mode - Active Job Full View */}
          {activeJobDashboard ? (
            <div className={`${dashboardAnimating ? 'dashboard-enter' : 'dashboard-enter-active'}`}>
              {/* Dashboard Header with Back Button - Ultra Compact */}
              <div className="flex items-center gap-2 mb-1">
                <button
                  onClick={exitDashboardMode}
                  className="flex items-center gap-1 px-2 py-1 rounded text-gray-300 hover:text-white transition-all duration-300"
                  style={{ 
                    background: 'rgba(31, 41, 55, 0.5)', 
                    border: '1px solid rgba(55, 65, 81, 1)' 
                  }}
                >
                  <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '16px' }}>arrow_back</span>
                  <span className="text-[10px] font-medium">Back</span>
                </button>
                <h2 className="text-sm font-bold text-white">{activeJobDashboard.jobTitle}</h2>
                <div className="flex items-center gap-1">
                  <span className="px-1 py-0.5 bg-indigo-900/30 text-indigo-300 rounded text-[9px] border border-indigo-700/50">
                    {activeJobDashboard.selectedCategory}
                  </span>
                  <span className="px-1 py-0.5 bg-gray-800 text-gray-400 rounded text-[9px] border border-gray-700">
                    {activeJobDashboard.selectedExperience}
                  </span>
                </div>
              </div>

              {/* Dashboard Grid Layout - Single Screen */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-2" style={{ height: 'calc(100vh - 100px)' }}>
                {/* Left Column - Job Details & Actions Combined */}
                <div className="lg:col-span-1 flex flex-col gap-2 overflow-hidden">
                  {/* Job Info + Actions Combined Card */}
                  <div 
                    className="rounded-lg p-2.5 animate-fadeInUp flex-shrink-0"
                    style={{ 
                      background: 'rgba(30, 41, 59, 0.4)', 
                      border: '1px solid rgba(71, 85, 105, 0.5)',
                      backdropFilter: 'blur(12px)'
                    }}
                  >
                    {/* Info Grid */}
                    <div className="space-y-1.5 mb-2">
                      <div className="flex items-start gap-2">
                        <span className="text-[9px] uppercase text-indigo-400 w-16 flex-shrink-0 pt-0.5">Pay</span>
                        <p className="text-[11px] text-gray-200 flex-1">{activeJobDashboard.compensation || 'N/A'}</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-[9px] uppercase text-indigo-400 w-16 flex-shrink-0 pt-0.5">Skills</span>
                        <p className="text-[11px] text-gray-200 flex-1 line-clamp-2">{activeJobDashboard.skill || 'N/A'}</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-[9px] uppercase text-indigo-400 w-16 flex-shrink-0 pt-0.5">Benefits</span>
                        <p className="text-[11px] text-gray-200 flex-1 line-clamp-2">{activeJobDashboard.benefits || 'N/A'}</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-[9px] uppercase text-indigo-400 w-16 flex-shrink-0 pt-0.5">Posted</span>
                        <p className="text-[11px] text-gray-200">
                          {activeJobDashboard.createdAt ? new Date(activeJobDashboard.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : 'N/A'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="space-y-1.5 pt-2" style={{ borderTop: '1px solid rgba(55, 65, 81, 0.4)' }}>
                      <button
                        onClick={() => handleRunMatchingEngine(activeJobDashboard)}
                        disabled={isRunningMatching && matchingJobId === activeJobDashboard.id}
                        className="w-full flex items-center justify-center gap-1.5 text-[10px] py-1.5 px-2 rounded text-white transition-all duration-300"
                        style={{
                          background: isRunningMatching && matchingJobId === activeJobDashboard.id 
                            ? 'linear-gradient(to right, #888 0%, #666 100%)'
                            : 'linear-gradient(to right, #a855f7 0%, #6366f1 100%)'
                        }}
                      >
                        <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '14px' }}>psychology</span>
                        {isRunningMatching && matchingJobId === activeJobDashboard.id ? 'Running...' : 'Match'}
                      </button>
                      <div className="flex gap-1">
                        <button
                          onClick={handleEditJob}
                          className="flex-1 flex items-center justify-center gap-1 text-[10px] py-1 px-1.5 rounded border border-gray-700 bg-gray-800 hover:bg-gray-700 text-gray-300"
                        >
                          <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '12px' }}>edit</span>
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteJob(activeJobDashboard.id, activeJobDashboard.jobTitle)}
                          className="flex-1 flex items-center justify-center gap-1 text-[10px] py-1 px-1.5 rounded border border-red-700/50 bg-red-900/20 hover:bg-red-900/40 text-red-400"
                        >
                          <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '12px' }}>delete</span>
                          Del
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Job Description Card - Limited Height */}
                  <div 
                    className="rounded-lg p-2.5 animate-fadeInUp stagger-1 flex-shrink-0 overflow-hidden"
                    style={{ 
                      background: 'rgba(30, 41, 59, 0.4)', 
                      border: '1px solid rgba(71, 85, 105, 0.5)',
                      backdropFilter: 'blur(12px)',
                      maxHeight: '180px'
                    }}
                  >
                    <h3 className="text-[10px] font-semibold text-white mb-1.5 flex items-center gap-1">
                      <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '12px' }} className="text-indigo-400">description</span>
                      Description
                    </h3>
                    <p className="text-[10px] text-gray-300 leading-relaxed whitespace-pre-wrap overflow-y-auto" style={{ scrollbarWidth: 'thin', maxHeight: '145px' }}>
                      {activeJobDashboard.jobDescription || 'No description available'}
                    </p>
                  </div>
                </div>

                {/* Right Column - Candidates Section (wider) */}
                <div className="lg:col-span-3 flex flex-col overflow-hidden">
                  <div 
                    className="rounded-lg overflow-hidden animate-fadeInUp stagger-1 flex flex-col"
                    style={{ 
                      background: 'rgba(30, 41, 59, 0.4)', 
                      border: '1px solid rgba(71, 85, 105, 0.5)',
                      backdropFilter: 'blur(12px)',
                      maxHeight: 'calc(100vh - 100px)'
                    }}
                  >
                    {/* Candidates Header - Compact */}
                    <div className="p-2.5 flex items-center justify-between flex-shrink-0" style={{ borderBottom: '1px solid rgba(55, 65, 81, 0.5)' }}>
                      <h3 className="text-xs font-semibold text-white flex items-center gap-1.5">
                        <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '16px' }} className="text-indigo-400">groups</span>
                        Private Screening Candidates
                        {jobCandidates[activeJobDashboard.id] && (
                          <span className="ml-1.5 px-1.5 py-0.5 bg-indigo-900/50 text-indigo-300 rounded-full text-[10px]">
                            {jobCandidates[activeJobDashboard.id].length}
                          </span>
                        )}
                      </h3>
                      <div className="flex items-center gap-2">
                        {/* Search: collapsed icon by default, expands into an input on click */}
                        {candidateSearchOpen ? (
                          <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-900/30 border border-indigo-500/30">
                            <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '14px' }} className="text-indigo-300">search</span>
                            <input
                              type="text"
                              autoFocus
                              value={candidateSearchQuery}
                              onChange={(e) => setCandidateSearchQuery(e.target.value)}
                              placeholder="Search name or email"
                              className="bg-transparent border-none outline-none text-[11px] text-white placeholder-gray-500 w-32"
                            />
                            <button
                              onClick={() => { setCandidateSearchOpen(false); setCandidateSearchQuery(''); }}
                              className="text-gray-400 hover:text-white flex items-center"
                              title="Close search"
                            >
                              <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '14px' }}>close</span>
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setCandidateSearchOpen(true)}
                            className="text-[10px] text-gray-400 hover:text-indigo-300 flex items-center px-2 py-1 rounded-lg hover:bg-indigo-900/30 transition-colors"
                            title="Search candidates"
                          >
                            <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '14px' }}>search</span>
                          </button>
                        )}
                        {/* Video duration filter */}
                        <div
                          className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-colors ${candidateDurationFilter !== 'all' ? 'bg-indigo-900/40 border border-indigo-500/40' : 'hover:bg-indigo-900/30'}`}
                          title="Filter by interview video length"
                        >
                          <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '14px' }} className="text-indigo-300">timer</span>
                          <select
                            value={candidateDurationFilter}
                            onChange={(e) => setCandidateDurationFilter(e.target.value)}
                            className="bg-transparent border-none outline-none text-[10px] text-gray-300 cursor-pointer"
                          >
                            {DURATION_FILTERS.map(opt => (
                              <option key={opt.value} value={opt.value} className="bg-slate-800 text-white">
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {/* Sort Toggle */}
                        <button
                          onClick={() => setCandidateSortOrder(prev => prev === 'newest' ? 'oldest' : 'newest')}
                          className="text-[10px] text-gray-400 hover:text-indigo-300 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-indigo-900/30 transition-colors"
                          title={candidateSortOrder === 'newest' ? 'Sorted: Newest first' : 'Sorted: Oldest first'}
                        >
                          <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '14px' }}>
                            {candidateSortOrder === 'newest' ? 'arrow_downward' : 'arrow_upward'}
                          </span>
                          {candidateSortOrder === 'newest' ? 'Newest' : 'Oldest'}
                        </button>
                        <button
                          onClick={() => fetchCandidatesForJob(activeJobDashboard.id)}
                          className="text-[10px] text-indigo-400 hover:text-indigo-300 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-indigo-900/30 transition-colors"
                        >
                          <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '14px' }}>refresh</span>
                          Refresh
                        </button>
                      </div>
                    </div>

                    {/* Candidates List - Fixed Height with Internal Scrolling */}
                    <div className="p-2.5 overflow-y-auto" style={{ scrollbarWidth: 'thin', maxHeight: 'calc(100vh - 300px)' }}>
                      {loadingCandidates ? (
                        <div className="space-y-1">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <div
                              key={i}
                              className="relative bg-white/5 backdrop-blur-xl rounded overflow-hidden"
                            >
                              <div className="px-3 py-1.5">
                                <div className="flex items-center gap-2">
                                  {/* Avatar skeleton */}
                                  <div className="w-8 h-8 rounded-full bg-gray-700/50 animate-pulse"></div>
                                  
                                  {/* Name skeleton */}
                                  <div className="min-w-[120px]">
                                    <div className="h-3 bg-gray-700/50 rounded animate-pulse w-3/4"></div>
                                  </div>
                                  
                                  {/* Email skeleton */}
                                  <div className="min-w-[180px]">
                                    <div className="h-3 bg-gray-700/50 rounded animate-pulse w-full"></div>
                                  </div>
                                  
                                  {/* Timestamp skeleton */}
                                  <div className="flex-1">
                                    <div className="h-3 bg-gray-700/50 rounded animate-pulse w-2/3"></div>
                                  </div>
                                  
                                  {/* Button skeletons */}
                                  <div className="flex items-center gap-1.5">
                                    <div className="w-16 h-6 bg-gray-700/50 rounded animate-pulse"></div>
                                    <div className="w-16 h-6 bg-gray-700/50 rounded animate-pulse"></div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Shimmer effect */}
                              <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>
                            </div>
                          ))}
                        </div>
                      ) : (jobCandidates[activeJobDashboard.id] && jobCandidates[activeJobDashboard.id].length > 0) ? (
                        dashboardCandidatesView.length === 0 ? (
                          <div className="text-center py-6 text-xs text-gray-400">
                            {candidateDurationFilter !== 'all'
                              ? `No candidates with a ${(DURATION_FILTERS.find(f => f.value === candidateDurationFilter) || {}).label || ''} video${candidateSearchQuery.trim() ? ` matching "${candidateSearchQuery}"` : ''}`
                              : `No candidates match "${candidateSearchQuery}"`}
                          </div>
                        ) : (
                        <div className="space-y-1">
                          {dashboardCandidatesView.map((candidate, idx) => (
                            <CandidateRow
                              key={candidate.id ?? idx}
                              candidate={candidate}
                              idx={idx}
                              job={activeJobDashboard}
                              jobTitle={activeJobDashboard.jobTitle}
                              jobId={activeJobDashboard.id}
                              isReinviting={reinvitingId === candidate.id}
                              durationSec={candidate._videoURL ? videoDurations[candidate._videoURL] : undefined}
                              onWatchVideo={onWatchVideo}
                              onGmail={onGmail}
                              onReinvite={onReinviteRow}
                              onView={onViewRow}
                            />
                          ))}
                        </div>
                        )
                      ) : (
                        <div className="flex flex-col items-center justify-center py-6 text-center">
                          <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '36px' }} className="text-gray-600 mb-2">person_search</span>
                          <p className="text-gray-400 text-xs font-medium">No candidates yet</p>
                          <p className="text-gray-500 text-[10px] mt-0.5">Add candidates below or run matching engine</p>
                        </div>
                      )}
                    </div>

                    {/* Quick Add Candidate Form - Compact */}
                    <div className="p-2.5 flex-shrink-0" style={{ borderTop: '1px solid rgba(55, 65, 81, 0.5)', background: 'rgba(31, 41, 55, 0.3)' }}>
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          const candidateName = e.target.elements.candidateName.value;
                          const candidateEmail = e.target.elements.candidateEmail.value;
                          const resumeFile = e.target.elements.resumeFile?.files?.[0] || null;

                          // Use activeJobDashboard directly for the submission
                          const jobToUse = activeJobDashboard;
                          if (jobToUse) {
                            setSelectedJob(jobToUse);
                            // Pass the job data directly to avoid state timing issues
                            handlePrivateScreeningMailSubmitWithJob(e, jobToUse, candidateName, candidateEmail, resumeFile);
                          }
                        }}
                        className="flex flex-wrap gap-2 items-center"
                      >
                        <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '14px' }} className="text-indigo-400">person_add</span>
                        <input
                          type="text"
                          name="candidateName"
                          placeholder="Name"
                          className="flex-1 min-w-[100px] text-white px-2 py-1.5 border border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-gray-800 text-xs"
                          required
                        />
                        <input
                          type="email"
                          name="candidateEmail"
                          placeholder="Email"
                          className="flex-1 min-w-[140px] text-white px-2 py-1.5 border border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-gray-800 text-xs"
                          required
                        />
                        {/* Resume upload temporarily disabled — backend parser handoff is incomplete.
                            Re-enable this <label> block once the parser team finishes the GET route. */}
                        {false && (
                          <label
                            className="flex items-center gap-1 px-2 py-1.5 text-[11px] text-gray-300 bg-gray-800 border border-gray-700 rounded-lg cursor-pointer hover:bg-gray-700"
                            title="Attach candidate resume (PDF, optional)"
                          >
                            <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '14px' }}>attach_file</span>
                            <span className="hidden sm:inline">Resume</span>
                            <input
                              type="file"
                              name="resumeFile"
                              accept="application/pdf,.pdf"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;

                                const isPdf = file.type === 'application/pdf' ||
                                  file.name.toLowerCase().endsWith('.pdf');

                                if (!isPdf) {
                                  setToast({ show: true, message: '❌ Resume must be a PDF', type: 'error' });
                                  setTimeout(() => setToast({ show: false, message: '', type: 'error' }), 5000);
                                  e.target.value = '';
                                  return;
                                }
                                if (file.size > MAX_RESUME_SIZE_BYTES) {
                                  setToast({ show: true, message: '❌ Resume must be 10 MB or smaller', type: 'error' });
                                  setTimeout(() => setToast({ show: false, message: '', type: 'error' }), 5000);
                                  e.target.value = '';
                                  return;
                                }

                                setToast({ show: true, message: `✅ Resume selected: ${file.name}`, type: 'success' });
                                setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
                              }}
                            />
                          </label>
                        )}
                        <button
                          type="submit"
                          disabled={isSending}
                          className="px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-all duration-300 disabled:opacity-50 flex items-center gap-1"
                          style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}
                        >
                          <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '14px' }}>send</span>
                          {isSending ? '...' : 'Send'}
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Normal Job List View */
            <div className="space-y-3">
              <h2 className="text-xl font-bold text-white">Active Job Postings</h2>

              {/* Loading State */}
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-12 space-y-4">
                  <span className="material-symbols-outlined animate-spin text-indigo-400 text-4xl">progress_activity</span>
                  <p className="text-gray-400 text-sm">Loading your job postings...</p>
                  <p className="text-gray-500 text-xs">Fetching company data...</p>
                </div>
              ) : !localStorage.getItem("CompanyProfileID") ? (
                /* Authentication/Company Error State */
                <div className="flex flex-col items-center justify-center py-12 space-y-4 text-center">
                  <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '48px' }} className="text-red-500">error_outline</span>
                  <h3 className="text-lg font-semibold text-white">Company Profile Not Found</h3>
                  <p className="text-gray-400 text-sm max-w-md">
                    Unable to load your company profile. Please try refreshing the page or contact support if the issue persists.
                  </p>
                  <div className="flex gap-3">
                    <button 
                      onClick={() => window.location.reload()} 
                      className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '18px' }}>refresh</span>
                      Refresh Page
                    </button>
                    <Link to="/company">
                      <button className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors">
                        <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '18px' }}>home</span>
                        Company Home
                      </button>
                    </Link>
                  </div>
                </div>
              ) : jobDetails.length === 0 ? (
                /* Empty State */
                <div className="flex flex-col items-center justify-center py-12 space-y-4 text-center">
                  <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '48px' }} className="text-gray-600">work_outline</span>
                  <h3 className="text-lg font-semibold text-white">No job postings yet</h3>
                  <p className="text-gray-400 text-sm max-w-md">
                    Create your first job posting to start finding great candidates
                  </p>
                  <Link to="/JobDetails">
                    <button className="mt-4 flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition-colors">
                      <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '18px' }}>add</span>
                      Create Job Posting
                    </button>
                  </Link>
                </div>
              ) : (
                /* Job Cards */
                <div className="space-y-2">
                  {jobDetails.map((job, index) => (
                  <div 
                    key={job.id}
                    className={`rounded-lg overflow-hidden transition-all duration-500 ${dashboardAnimating ? 'job-card-hidden' : 'job-card-visible'}`}
                    style={{
                      background: 'rgba(30, 41, 59, 0.4)',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(71, 85, 105, 0.4)'
                    }}
                  >
                    {/* Job Header Row */}
                    <div 
                      className="grid grid-cols-[1fr_auto_auto] gap-3 items-center p-3 hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="font-semibold text-white text-sm">{job.jobTitle}</div>
                        <div className="hidden md:flex items-center gap-1.5">
                          <span className="px-2 py-0.5 bg-indigo-900/30 text-indigo-300 rounded text-xs border border-indigo-700/50">
                            {job.selectedCategory}
                          </span>
                          <span className="px-2 py-0.5 bg-gray-800 text-gray-400 rounded text-xs border border-gray-700">
                            {job.selectedExperience}
                          </span>
                        </div>
                      </div>
                      <div className="hidden md:block text-gray-400 text-xs">
                        {job.updatedAt ? new Date(job.updatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A'}
                      </div>
                      
                      {/* Open Dashboard Button */}
                      <button
                        onClick={() => activateDashboardMode(job)}
                        className="flex items-center gap-1.5 text-xs py-1.5 px-3 rounded-lg transition-all duration-300 text-white"
                        style={{
                          background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                          boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)'
                        }}
                      >
                        <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '16px' }}>dashboard</span>
                        <span className="hidden sm:inline">Open Dashboard</span>
                      </button>
                    </div>
                  </div>
                ))}
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Fixed AI Button - Bottom Right (Outside Container) */}
      {!activeJobDashboard && (
        <div className="fixed bottom-8 right-8 z-[9999] ai-button-slide-in">
          <button
            onClick={() => navigate('/spotgpt')}
            className="group relative flex items-center gap-3 px-5 py-3.5 rounded-2xl font-medium transition-all duration-500 animate-breathe overflow-hidden shadow-2xl"
            style={{
              background: 'rgba(30, 41, 59, 0.95)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(99, 102, 241, 0.4)',
              boxShadow: '0 4px 20px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            }}
          >
            {/* Shimmer overlay on hover */}
            <div 
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ai-button-shimmer"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, rgba(99, 102, 241, 0.1) 50%, transparent 100%)',
                backgroundSize: '200% 100%'
              }}
            ></div>

            {/* Icon with subtle glow */}
            <div className="relative">
              <div 
                className="absolute inset-0 rounded-full blur-md opacity-50 group-hover:opacity-75 transition-opacity"
                style={{ background: 'rgba(99, 102, 241, 0.4)' }}
              ></div>
              <span 
                className="relative block text-indigo-300 group-hover:text-indigo-200 transition-colors"
                style={{ fontFamily: 'Material Symbols Outlined', fontSize: '26px' }}
              >
                psychology
              </span>
            </div>

            {/* Text */}
            <div className="relative flex flex-col items-start">
              <span className="text-sm text-white font-semibold group-hover:text-indigo-100 transition-colors">
                Ask our AI
              </span>
              <span className="text-[10px] text-gray-400 font-normal">
                Get instant help
              </span>
            </div>

            {/* Live indicator dot */}
            <div className="relative">
              <span 
                className="absolute inset-0 rounded-full animate-ping"
                style={{ background: '#22c55e', opacity: 0.4 }}
              ></span>
              <span 
                className="relative block w-2 h-2 rounded-full"
                style={{ background: '#22c55e', boxShadow: '0 0 8px #22c55e' }}
              ></span>
            </div>

            {/* Hover glow effect */}
            <div 
              className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background: 'radial-gradient(circle at center, rgba(99, 102, 241, 0.1) 0%, transparent 70%)'
              }}
            ></div>
          </button>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div 
          className="fixed top-4 right-4 z-[99999]"
          style={{
            animation: 'slideInFromTop 0.3s ease-out'
          }}
        >
          <div 
            className="flex items-center gap-3 px-4 py-3 rounded-lg shadow-2xl min-w-[300px]"
            style={{
              background: toast.type === 'success' 
                ? 'rgba(34, 197, 94, 0.95)' 
                : 'rgba(239, 68, 68, 0.95)',
              backdropFilter: 'blur(16px)',
              border: `1px solid ${toast.type === 'success' ? 'rgba(34, 197, 94, 0.5)' : 'rgba(239, 68, 68, 0.5)'}`,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}
          >
            <span 
              style={{ fontFamily: 'Material Symbols Outlined', fontSize: '24px' }} 
              className="text-white"
            >
              {toast.type === 'success' ? 'check_circle' : 'error'}
            </span>
            <span className="text-white text-sm font-medium flex-1">{toast.message}</span>
            <button
              onClick={() => setToast({ show: false, message: '', type: 'success' })}
              className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {isModalOpen && <BuyPremium closeModal={closeModal} />}
        {/* Video Preview Modal — uses an HTML5 player with controlsList="nodownload"
            so the player UI does not expose a download menu. Right-click is
            also blocked. Note: a determined user can still pull the URL from
            DevTools — this only prevents casual downloads. */}
        {videoModalUrl && ReactDOM.createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', zIndex: 9999 }}
            onClick={() => setVideoModalUrl(null)}
          >
            <div
              className="relative w-full max-w-4xl bg-gray-900 rounded-xl overflow-hidden"
              style={{ border: '1px solid rgba(71, 85, 105, 0.5)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-2.5" style={{ borderBottom: '1px solid rgba(55, 65, 81, 0.5)' }}>
                <h3 className="text-sm font-semibold text-white flex items-center gap-1.5">
                  <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '18px' }} className="text-green-400">videocam</span>
                  Interview Video
                </h3>
                <button
                  onClick={() => setVideoModalUrl(null)}
                  className="text-gray-400 hover:text-white p-1 rounded hover:bg-gray-800"
                  title="Close"
                >
                  <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '20px' }}>close</span>
                </button>
              </div>
              <div className="bg-black">
                <video
                  src={videoModalUrl}
                  controls
                  autoPlay
                  controlsList="nodownload noplaybackrate"
                  disablePictureInPicture
                  onContextMenu={(e) => e.preventDefault()}
                  className="w-full max-h-[75vh] block"
                />
              </div>
            </div>
          </div>,
          document.body
        )}
        {/* Reinvite Confirmation Modal */}
        {reinviteTarget && ReactDOM.createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              backdropFilter: 'blur(10px)',
              zIndex: 99999
            }}
            onClick={() => setReinviteTarget(null)}
          >
            <div
              className="w-full max-w-md rounded-xl shadow-2xl relative animate-fadeInUp"
              style={{
                background: 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(71, 85, 105, 0.6)',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.7)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-4 border-b border-gray-700 flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}
                >
                  <span className="material-symbols-outlined text-white" style={{ fontSize: '20px' }}>forward_to_inbox</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-bold text-white">Send Reinvitation?</h3>
                  <p className="text-[11px] text-gray-400">A fresh interview link will be generated.</p>
                </div>
                <button
                  onClick={() => setReinviteTarget(null)}
                  className="p-1.5 rounded-full text-gray-400 hover:bg-gray-800 transition-all"
                  title="Close"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Body */}
              <div className="p-4 space-y-3">
                <div
                  className="rounded-lg p-3 flex items-center gap-3"
                  style={{ background: 'rgba(31, 41, 55, 0.5)', border: '1px solid rgba(55, 65, 81, 1)' }}
                >
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {reinviteTarget.candidate.candidateName?.charAt(0).toUpperCase() || 'C'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white font-medium truncate">{reinviteTarget.candidate.candidateName}</p>
                    <p className="text-[11px] text-gray-400 truncate flex items-center gap-1">
                      <span className="material-symbols-outlined text-xs">mail</span>
                      {reinviteTarget.candidate.candidateEmail}
                    </p>
                  </div>
                </div>

                <div
                  className="rounded-lg p-3 flex items-start gap-2"
                  style={{ background: 'rgba(99, 102, 241, 0.08)', border: '1px solid rgba(99, 102, 241, 0.3)' }}
                >
                  <span className="material-symbols-outlined text-indigo-300 flex-shrink-0" style={{ fontSize: '18px' }}>info</span>
                  <div className="text-[11px] text-gray-300 leading-relaxed">
                    <p>This will:</p>
                    <ul className="mt-1 space-y-0.5 list-disc pl-4">
                      <li>Create a brand-new interview slot with a fresh link</li>
                      <li>Email the candidate the new link</li>
                      <li>
                        Deduct{' '}
                        <span className="text-indigo-300 font-semibold">200 credits</span>
                        {' '}from your balance
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="text-[11px] text-gray-400 flex items-center justify-between px-1">
                  <span>Current balance</span>
                  <span className="text-white font-semibold">{point} credits</span>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-700 flex items-center justify-end gap-2">
                <button
                  onClick={() => setReinviteTarget(null)}
                  className="px-4 py-2 text-xs font-medium text-gray-300 bg-gray-700/50 hover:bg-gray-700 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmReinviteCandidate}
                  className="px-4 py-2 text-xs font-medium text-white rounded-lg transition-all flex items-center gap-1.5"
                  style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}
                >
                  <span className="material-symbols-outlined text-sm">send</span>
                  Send Reinvite
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
        {/* Unified View & Screen Modal */}
        {showUnifiedModal && selectedJob && ReactDOM.createPortal(
          <div 
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              backdropFilter: 'blur(10px)',
              zIndex: 99999
            }}
          >
            <div 
              className="w-full max-w-3xl rounded-xl shadow-2xl relative flex flex-col"
              style={{
                background: 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(71, 85, 105, 0.6)',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.7)',
                maxHeight: '75vh'
              }}
            >
              {/* Fixed Header */}
              <div className="p-3 border-b border-gray-700 flex justify-between items-center flex-shrink-0">
                <h3 className="text-base font-bold text-white">Job Details & Private Screening</h3>
                <button
                  onClick={handleCloseModal}
                  className="p-1.5 rounded-full text-gray-400 hover:bg-gray-800 transition-all duration-300"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Scrollable Content Area */}
              <div 
                className="flex-1 overflow-y-auto modal-scroll"
                style={{
                  scrollBehavior: 'smooth',
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(55, 65, 81, 1) rgba(17, 24, 39, 0.5)'
                }}
              >
                <style>{`
                  /* Custom Scrollbar Styling */
                  .modal-scroll::-webkit-scrollbar {
                    width: 8px;
                  }
                  .modal-scroll::-webkit-scrollbar-track {
                    background: rgba(17, 24, 39, 0.5);
                    border-radius: 10px;
                  }
                  .modal-scroll::-webkit-scrollbar-thumb {
                    background: rgba(55, 65, 81, 1);
                    border-radius: 10px;
                  }
                  .modal-scroll::-webkit-scrollbar-thumb:hover {
                    background: rgba(75, 85, 99, 1);
                  }
                `}</style>
                
                <div className="p-3">
                  {/* Job Header - Compact */}
                  <div 
                    className="mb-3 p-3 rounded-lg"
                    style={{
                      background: 'rgba(31, 41, 55, 0.5)',
                      border: '1px solid rgba(55, 65, 81, 1)'
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <h2 className="text-base font-bold text-white mb-2">{selectedJob.jobTitle}</h2>
                        <div className="flex flex-wrap gap-1.5">
                          <span className="px-2 py-0.5 bg-indigo-900/50 text-indigo-300 rounded text-xs font-medium border border-indigo-700">
                            {selectedJob.selectedCategory}
                          </span>
                          <span className="px-2 py-0.5 bg-indigo-900/50 text-indigo-300 rounded text-xs font-medium border border-indigo-700">
                            {selectedJob.workMode || 'Full-time'}
                          </span>
                          <span className="px-2 py-0.5 bg-indigo-900/50 text-indigo-300 rounded text-xs font-medium border border-indigo-700">
                            {selectedJob.selectedExperience}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Job Info Grid - 2x2 Compact Layout */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="p-2 rounded-lg" style={{ background: 'rgba(31, 41, 55, 0.5)', border: '1px solid rgba(55, 65, 81, 1)' }}>
                      <h4 className="text-[10px] uppercase tracking-wide text-indigo-400 mb-1">Compensation</h4>
                      <h5 className="text-xs text-gray-200 font-medium">{selectedJob.compensation}</h5>
                    </div>
                    <div className="p-2 rounded-lg" style={{ background: 'rgba(31, 41, 55, 0.5)', border: '1px solid rgba(55, 65, 81, 1)' }}>
                      <h4 className="text-[10px] uppercase tracking-wide text-indigo-400 mb-1">Skills Required</h4>
                      <h5 className="text-xs text-gray-200 font-medium">{selectedJob.skill}</h5>
                    </div>
                    <div className="p-2 rounded-lg" style={{ background: 'rgba(31, 41, 55, 0.5)', border: '1px solid rgba(55, 65, 81, 1)' }}>
                      <h4 className="text-[10px] uppercase tracking-wide text-indigo-400 mb-1">Benefits</h4>
                      <h5 className="text-xs text-gray-200 font-medium">{selectedJob.benefits}</h5>
                    </div>
                    <div className="p-2 rounded-lg" style={{ background: 'rgba(31, 41, 55, 0.5)', border: '1px solid rgba(55, 65, 81, 1)' }}>
                      <h4 className="text-[10px] uppercase tracking-wide text-indigo-400 mb-1">Posted</h4>
                      <h5 className="text-xs text-gray-200 font-medium">
                        {selectedJob.createdAt ? (() => {
                          const date = new Date(selectedJob.createdAt);
                          return !isNaN(date.getTime()) ? date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : selectedJob.createdAt;
                        })() : 'N/A'}
                      </h5>
                    </div>
                  </div>

                  {/* Job Description - Compact */}
                  <div 
                    className="mb-3 p-3 rounded-lg"
                    style={{ background: 'rgba(31, 41, 55, 0.5)', border: '1px solid rgba(55, 65, 81, 1)' }}
                  >
                    <h3 className="text-[10px] uppercase tracking-wide text-indigo-400 mb-1.5">Job Description</h3>
                    <h4 className="text-xs text-gray-300 leading-relaxed whitespace-pre-wrap">{selectedJob.jobDescription}</h4>
                  </div>

                  {/* Download JD - Compact Inline */}
                  <div 
                    className="mb-3 p-2.5 rounded-lg flex items-center justify-between gap-3"
                    style={{ background: 'rgba(31, 41, 55, 0.5)', border: '1px solid rgba(55, 65, 81, 1)' }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-indigo-400" style={{ fontFamily: 'Material Symbols Outlined', fontSize: '18px' }}>download</span>
                      <span className="text-xs text-gray-300">Download JD</span>
                    </div>
                    <select 
                      className="text-xs bg-gray-800 text-gray-200 border border-gray-700 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      onChange={(e) => {
                        const format = e.target.value;
                        if (format === 'txt') {
                          const blob = new Blob([selectedJob.jobDescription], { type: 'text/plain' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${selectedJob.jobTitle}_JD.txt`;
                          a.click();
                        }
                      }}
                      defaultValue=""
                    >
                      <option value="" disabled>Select format</option>
                      <option value="txt">Text (.txt)</option>
                    </select>
                  </div>

                  {/* Private Screening Form - Compact */}
                  <div 
                    className="p-3 rounded-lg"
                    style={{
                      background: 'rgba(31, 41, 55, 0.5)',
                      border: '1px solid rgba(55, 65, 81, 1)',
                      borderTop: '2px solid #6366f1'
                    }}
                  >
                    <h3 className="text-xs font-semibold text-white mb-2.5">Send Private Screening Invitation</h3>
                    <form onSubmit={handlePrivateScreeningMailSubmit}>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div>
                          <label htmlFor="candidateName" className="block text-[10px] text-gray-400 mb-1">Name</label>
                          <input
                            type="text"
                            id="candidateName"
                            className="w-full text-white px-2 py-1.5 border border-gray-700 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-gray-800 text-xs"
                            placeholder="Candidate name"
                            required
                          />
                        </div>
                        <div>
                          <label htmlFor="candidateEmail" className="block text-[10px] text-gray-400 mb-1">Email</label>
                          <input
                            type="email"
                            id="candidateEmail"
                            className="w-full text-white px-2 py-1.5 border border-gray-700 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-gray-800 text-xs"
                            placeholder="Candidate email"
                            required
                          />
                        </div>
                      </div>
                      <button
                        type="submit"
                        className="w-full px-3 py-1.5 text-xs font-semibold text-white rounded transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                        style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}
                        disabled={isSending}
                      >
                        {isSending ? 'Sending...' : 'Send Invitation'}
                      </button>
                    </form>
                  </div>
                </div>
              </div>

              {/* Fixed Footer */}
              <div className="p-2 border-t border-gray-700 flex-shrink-0">
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={handleCloseModal}
                    className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded hover:bg-gray-700 transition-all duration-300"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleEditJob}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 border border-indigo-600 rounded hover:bg-indigo-700 transition-all duration-300"
                  >
                    Edit Job
                  </button>
                </div>
              </div>
            </div>
          </div>
        , document.body)}

        {/* Enhanced Job Editing Modal */}
        {showModal && selectedJob && ReactDOM.createPortal(
          <div 
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              backdropFilter: 'blur(12px)',
              zIndex: 100001
            }}
          >
            <div 
              className="w-full max-w-2xl rounded-xl shadow-2xl relative flex flex-col"
              style={{
                background: 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(71, 85, 105, 0.6)',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.7)',
                maxHeight: '75vh'
              }}
            >
              {/* Fixed Header */}
                <div
                className="p-4 border-b border-gray-700 flex justify-between items-center flex-shrink-0"
                style={{ background: 'rgba(51, 65, 85, 0.35)' }}
              >
                <div className="min-w-0">
                  <h3 className="text-xl font-bold text-white">Edit Job</h3>
                  <p className="text-xs text-gray-400 truncate">{selectedJob?.jobTitle}</p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="p-1.5 rounded-full text-gray-400 hover:bg-gray-800 transition-all duration-300 hover:scale-110"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Scrollable Content Area */}
              <div 
                className="flex-1 overflow-y-auto"
                style={{
                  scrollBehavior: 'smooth',
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(55, 65, 81, 1) rgba(17, 24, 39, 0.5)'
                }}
              >
                <div className="p-4">
                  <JobPostForm
                    initialValues={selectedJob}
                    onCancel={handleCloseModal}
                    embedded
                  />
                </div>
              </div>
            </div>
          </div>
        , document.body)}

        {/* Matching Results Modal */}
        {showMatchingResultModal && (
          <div 
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              backdropFilter: 'blur(10px)',
              zIndex: 99999
            }}
          >
            <div 
              className="w-full max-w-3xl rounded-xl shadow-2xl relative overflow-hidden max-h-[85vh] flex flex-col"
              style={{
                background: 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(71, 85, 105, 0.6)',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.7)'
              }}
            >
              <div className="p-6 border-b border-gray-700 flex justify-between items-center" style={{ background: 'rgba(51, 65, 85, 0.5)' }}>
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    ⚡ Matching Engine Results
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Found {matchingResults.length} matching candidates | {selectedMatchingCandidates.length} selected
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowMatchingResultModal(false);
                    setSelectedMatchingCandidates([]);
                  }}
                  className="p-2 rounded-full text-white hover:bg-white/20 transition-all duration-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto flex-1">
                {/* Auto-filter Toggle & Selection Controls */}
                <div className="p-4 mb-4 rounded-xl flex flex-wrap items-center justify-between gap-4" style={{ background: 'rgba(153, 0, 255, 0.1)', border: '1px solid rgba(153, 0, 255, 0.2)' }}>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoFilterEnabled}
                        onChange={(e) => setAutoFilterEnabled(e.target.checked)}
                        className="w-4 h-4 rounded"
                        style={{ accentColor: '#9900ff' }}
                      />
                      <span className="text-purple-900 text-sm font-medium">Auto-filter on match</span>
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={selectAllMatchingCandidates}
                      className="px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-all"
                    >
                      Select All
                    </button>
                    <button
                      onClick={deselectAllMatchingCandidates}
                      className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-all"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>

                <div className="p-3 mb-4 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-blue-800 text-sm">
                    💡 Select candidates individually or use "Select All" then choose an action:
                    <strong> Filter</strong> (add to filtered list) or <strong>Select for Screening</strong> (private screening).
                  </p>
                </div>

                <div className="space-y-3">
                  {matchingResults.map((candidate, idx) => {
                    const email = candidate.email || candidate.candidate_email;
                    const name = candidate.full_name || candidate.name || email?.split('@')[0] || 'Unknown';
                    const score = candidate.overall_score || candidate.score;
                    const isChecked = selectedMatchingCandidates.some(c => (c.email || c.candidate_email) === email);
                    
                    return (
                      <div 
                        key={idx}
                        onClick={() => toggleCandidateSelection(candidate)}
                        className="p-4 rounded-xl border transition-all duration-200 hover:shadow-md cursor-pointer"
                        style={{
                          background: isChecked ? 'rgba(153, 0, 255, 0.08)' : 'rgba(255, 255, 255, 0.8)',
                          borderColor: isChecked ? 'rgba(153, 0, 255, 0.5)' : 'rgba(153, 0, 255, 0.2)',
                          transform: isChecked ? 'scale(1.01)' : 'scale(1)'
                        }}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleCandidateSelection(candidate)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-5 h-5 rounded"
                              style={{ accentColor: '#9900ff' }}
                            />
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                              style={{ background: 'linear-gradient(135deg, #9900ff 0%, #7700cc 100%)' }}
                            >
                              {name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <h4 className="font-semibold text-slate-800">{name}</h4>
                              <p className="text-sm text-slate-500">{email}</p>
                              {candidate.skills && (
                                <p className="text-xs text-slate-400 mt-1">
                                  Skills: {typeof candidate.skills === 'string' ? candidate.skills.slice(0, 50) : candidate.skills?.slice(0, 3).join(', ')}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {score !== undefined && (
                              <span 
                                className="px-3 py-1 rounded-full text-sm font-semibold text-white"
                                style={{
                                  background: score >= 0.8 ? '#22c55e' : score >= 0.6 ? '#eab308' : '#ef4444'
                                }}
                              >
                                {(score * 100).toFixed(0)}%
                              </span>
                            )}
                            {candidate.isFiltered && (
                              <span 
                                className="px-2 py-0.5 rounded text-xs font-medium"
                                style={{ background: 'rgba(249, 115, 22, 0.1)', color: '#f97316' }}
                              >
                                FILTERED
                              </span>
                            )}
                            {candidate.isSelected && (
                              <span 
                                className="px-2 py-0.5 rounded text-xs font-medium"
                                style={{ background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }}
                              >
                                SELECTED
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="p-6 border-t border-purple-200/50 flex flex-wrap justify-between gap-3">
                <div className="flex gap-3">
                  <button
                    onClick={filterSelectedCandidates}
                    disabled={selectedMatchingCandidates.length === 0 || isRunningMatching}
                    className="px-5 py-2.5 text-sm font-medium text-white rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' }}
                  >
                    {isRunningMatching ? '⏳ Processing...' : `⊗ Filter Selected (${selectedMatchingCandidates.length})`}
                  </button>
                  <button
                    onClick={selectCandidatesForScreening}
                    disabled={selectedMatchingCandidates.length === 0 || isRunningMatching}
                    className="px-5 py-2.5 text-sm font-medium text-white rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}
                  >
                    {isRunningMatching ? '⏳ Processing...' : `✓ Select for Screening (${selectedMatchingCandidates.length})`}
                  </button>
                </div>
                <button
                  onClick={() => {
                    setShowMatchingResultModal(false);
                    setSelectedMatchingCandidates([]);
                  }}
                  className="px-6 py-2.5 text-sm font-medium text-white rounded-xl transition-all duration-300"
                  style={{ background: 'linear-gradient(135deg, #9900ff 0%, #7700cc 100%)' }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Candidate Profile Modal */}
        {showProfileModal && ReactDOM.createPortal(
          <div 
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              backdropFilter: 'blur(10px)',
              zIndex: 99999
            }}
          >
            <div 
              className="w-full max-w-4xl rounded-xl shadow-2xl relative overflow-hidden"
              style={{
                background: 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(71, 85, 105, 0.6)',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.7)',
                maxHeight: '90vh'
              }}
            >
              {/* Header */}
              <div className="p-4 border-b border-gray-700 flex justify-between items-center" style={{ background: 'rgba(51, 65, 85, 0.5)' }}>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <span className="material-symbols-outlined text-2xl text-indigo-400">person</span>
                  Candidate Profile
                </h2>
                <button
                  onClick={closeProfileModal}
                  className="p-2 rounded-full text-white hover:bg-white/20 transition-all duration-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Content */}
              <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
                {loadingProfile ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <span className="material-symbols-outlined animate-spin text-indigo-400 text-5xl mb-4">progress_activity</span>
                    <p className="text-gray-300 text-lg">Loading profile...</p>
                  </div>
                ) : profileError ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <span className="material-symbols-outlined text-red-400 text-5xl mb-4">error</span>
                    <p className="text-red-300 text-lg mb-2">{profileError}</p>
                    <button
                      onClick={closeProfileModal}
                      className="mt-4 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
                    >
                      Close
                    </button>
                  </div>
                ) : selectedCandidateProfile?.noProfile ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <span className="material-symbols-outlined text-gray-500 text-5xl mb-4">person_off</span>
                    <p className="text-gray-300 text-lg mb-2">No Profile Available</p>
                    <p className="text-gray-400 text-sm mb-4">
                      {selectedCandidateProfile.fullName} ({selectedCandidateProfile.email})
                    </p>
                    <p className="text-gray-500 text-sm">
                      This candidate has not completed their profile registration yet.
                    </p>
                  </div>
                ) : selectedCandidateProfile ? (
                  <div className="space-y-4">
                    {/* Basic Info Card */}
                    <div className="p-4 rounded-lg" style={{ background: 'rgba(51, 65, 85, 0.4)', border: '1px solid rgba(71, 85, 105, 0.6)' }}>
                      <div className="flex items-start gap-4">
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-2xl flex-shrink-0">
                          {selectedCandidateProfile.fullName?.charAt(0).toUpperCase() || 'C'}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-white mb-1">{selectedCandidateProfile.fullName || 'N/A'}</h3>
                          <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                            <span className="material-symbols-outlined text-base">mail</span>
                            {selectedCandidateProfile.email}
                          </div>
                          {selectedCandidateProfile.mobileNumber && (
                            <div className="flex items-center gap-2 text-gray-400 text-sm">
                              <span className="material-symbols-outlined text-base">phone</span>
                              {selectedCandidateProfile.mobileNumber}
                            </div>
                          )}
                        </div>
                        {selectedCandidateProfile.hasCompletedInterview && (
                          <div className="px-3 py-1 bg-green-900/30 border border-green-700 rounded-lg">
                            <span className="text-green-400 text-xs font-medium">✓ Interview Completed</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Details Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Work Status */}
                      {selectedCandidateProfile.workStatus && (
                        <div className="p-3 rounded-lg" style={{ background: 'rgba(51, 65, 85, 0.4)', border: '1px solid rgba(71, 85, 105, 0.6)' }}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="material-symbols-outlined text-indigo-400 text-lg">work</span>
                            <h4 className="text-xs uppercase text-gray-400 font-semibold">Work Status</h4>
                          </div>
                          <p className="text-white text-sm">{selectedCandidateProfile.workStatus}</p>
                        </div>
                      )}
                      
                      {/* Total Experience */}
                      {selectedCandidateProfile.totalExperience && (
                        <div className="p-3 rounded-lg" style={{ background: 'rgba(51, 65, 85, 0.4)', border: '1px solid rgba(71, 85, 105, 0.6)' }}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="material-symbols-outlined text-indigo-400 text-lg">schedule</span>
                            <h4 className="text-xs uppercase text-gray-400 font-semibold">Experience</h4>
                          </div>
                          <p className="text-white text-sm">{selectedCandidateProfile.totalExperience}</p>
                        </div>
                      )}
                    </div>

                    {/* Skills */}
                    {selectedCandidateProfile.keySkills && (
                      <div className="p-4 rounded-lg" style={{ background: 'rgba(51, 65, 85, 0.4)', border: '1px solid rgba(71, 85, 105, 0.6)' }}>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="material-symbols-outlined text-indigo-400 text-lg">psychology</span>
                          <h4 className="text-sm uppercase text-gray-400 font-semibold">Key Skills</h4>
                        </div>
                        <p className="text-white text-sm leading-relaxed">{selectedCandidateProfile.keySkills}</p>
                      </div>
                    )}

                    {/* Description */}
                    {selectedCandidateProfile.description && (
                      <div className="p-4 rounded-lg" style={{ background: 'rgba(51, 65, 85, 0.4)', border: '1px solid rgba(71, 85, 105, 0.6)' }}>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="material-symbols-outlined text-indigo-400 text-lg">description</span>
                          <h4 className="text-sm uppercase text-gray-400 font-semibold">About</h4>
                        </div>
                        <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">{selectedCandidateProfile.description}</p>
                      </div>
                    )}

                    {/* Links */}
                    <div className="grid grid-cols-2 gap-4">
                      {selectedCandidateProfile.githubURL && (
                        <a
                          href={selectedCandidateProfile.githubURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-3 rounded-lg flex items-center gap-2 hover:bg-white/10 transition-colors"
                          style={{ background: 'rgba(51, 65, 85, 0.4)', border: '1px solid rgba(71, 85, 105, 0.6)' }}
                        >
                          <span className="material-symbols-outlined text-indigo-400 text-lg">code</span>
                          <span className="text-white text-sm">GitHub Profile</span>
                          <span className="material-symbols-outlined text-gray-400 text-sm ml-auto">open_in_new</span>
                        </a>
                      )}
                      
                      {selectedCandidateProfile.linkedinURL && (
                        <a
                          href={selectedCandidateProfile.linkedinURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-3 rounded-lg flex items-center gap-2 hover:bg-white/10 transition-colors"
                          style={{ background: 'rgba(51, 65, 85, 0.4)', border: '1px solid rgba(71, 85, 105, 0.6)' }}
                        >
                          <span className="material-symbols-outlined text-indigo-400 text-lg">work</span>
                          <span className="text-white text-sm">LinkedIn Profile</span>
                          <span className="material-symbols-outlined text-gray-400 text-sm ml-auto">open_in_new</span>
                        </a>
                      )}
                    </div>

                    {/* Interview Results Link */}
                    {selectedCandidateProfile.interviewSlot && (
                      <div className="p-4 rounded-lg" style={{ background: 'rgba(99, 102, 241, 0.1)', border: '1px solid rgba(99, 102, 241, 0.3)' }}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="material-symbols-outlined text-indigo-400 text-2xl">assignment</span>
                            <div>
                              <h4 className="text-white font-semibold">Interview Results Available</h4>
                              <p className="text-gray-400 text-sm">View detailed evaluation and analysis</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {buildResumeURL(selectedCandidateProfile.resumeURL) && (
                              <a
                                href={buildResumeURL(selectedCandidateProfile.resumeURL)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={buildResumeURL(selectedCandidateProfile.resumeURL)}
                                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-white text-sm font-medium transition-colors flex items-center gap-2"
                              >
                                <span className="material-symbols-outlined text-base">description</span>
                                View Resume
                                <span className="material-symbols-outlined text-base">open_in_new</span>
                              </a>
                            )}
                            <button
                              onClick={() => {
                                window.open(`/CompanyCandidateViewProfilev1/${encodeURIComponent(selectedCandidateProfile.email)}?slotId=${selectedCandidateProfile.interviewSlot.id}`, '_blank');
                              }}
                              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg text-white text-sm font-medium transition-colors flex items-center gap-2"
                            >
                              View Results
                              <span className="material-symbols-outlined text-base">open_in_new</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Interview History */}
                    {selectedCandidateProfile.allInterviewSlots && selectedCandidateProfile.allInterviewSlots.length > 0 && (
                      <div className="p-4 rounded-lg" style={{ background: 'rgba(51, 65, 85, 0.4)', border: '1px solid rgba(71, 85, 105, 0.6)' }}>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="material-symbols-outlined text-indigo-400 text-lg">history</span>
                          <h4 className="text-sm uppercase text-gray-400 font-semibold">Interview History</h4>
                          <span className="ml-auto text-gray-400 text-xs">{selectedCandidateProfile.allInterviewSlots.length} interview(s)</span>
                        </div>
                        <div className="space-y-2">
                          {selectedCandidateProfile.allInterviewSlots.slice(0, 5).map((slot, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 rounded bg-gray-800/50">
                              <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-gray-400 text-sm">event</span>
                                <span className="text-gray-300 text-xs">
                                  {slot.createdAt ? new Date(slot.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                                </span>
                              </div>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                slot.candidateAttendance ? 'bg-green-900/30 text-green-400' : 'bg-gray-700 text-gray-400'
                              }`}>
                                {slot.candidateAttendance ? 'Completed' : 'Scheduled'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        , document.body)}
      </div>
    </div>
  );
};

export default JobDetailsList;
